import { RUBRIC_LABELS } from "@type/training";
import type {
    CampaignStatus,
    EmployeeRow,
    RubricCriterion,
    RubricScores,
    SessionAttempt,
    SessionStatus,
    TrainingCampaign,
    TrainingCampaignDetail,
    TrainingPersona,
    TrainingSession,
    TrainingType,
    TranscriptTurn,
} from "@type/training";
import { getAssessment } from "@service/assessment";
import type { InterviewAssessment } from "@type/assessments";
import axios from "axios";
import { getCookie } from "@util/cookie";
import { catchErrorLog } from "@util/sentry";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

function authHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return headers;
}

function publicHeaders(): Record<string, string> {
    return { "Content-Type": "application/json" };
}

function unwrap<T>(data: unknown): T {
    const d = data as { data?: T } | T;
    if (d && typeof d === "object" && "data" in (d as Record<string, unknown>)) {
        return (d as { data: T }).data;
    }
    return d as T;
}

const PERSONAS: TrainingPersona[] = [
    {
        id: "cs_frustrated_customer",
        name: "Late Pos Laju Parcel",
        training_type: "customer_service",
        description: "A frustrated customer whose Pos Laju parcel is 4 days late. Tests empathy and resolution skills under pressure.",
        briefing:
            "You are a Pos Malaysia customer service agent. The customer's Pos Laju parcel was meant to arrive 4 days ago and they needed it for a family event. They're frustrated. De-escalate, acknowledge the delay, and offer a clear resolution.",
        assessment_id: "69a6d0c1-6638-407a-af72-26eaf9d3cdaa",
    },
    {
        id: "cs_confused_elder",
        name: "Elderly Counter Customer",
        training_type: "customer_service",
        description: "A senior customer at the counter trying to send a registered letter. Tests patience and clear, step-by-step communication.",
        briefing:
            "You are a Pos Malaysia counter agent. An elderly customer wants to send a registered letter but is confused by the form and the tracking option. Walk them through it patiently, check in often, and don't rush.",
        assessment_id: "654764e6-b067-4705-8303-abd20c58e1aa",
    },
    {
        id: "cs_refund_demander",
        name: "Lost Parcel Claim",
        training_type: "customer_service",
        description: "A customer whose parcel was marked delivered but never received. Demands full compensation. Tests firmness with empathy on claims policy.",
        briefing:
            "You are a Pos Malaysia customer service agent. A customer's parcel was scanned 'delivered' 3 days ago but they never received it. They want full compensation including the contents (RM 800). Apply claims policy fairly while showing empathy.",
        assessment_id: "2a56c588-0196-4351-8781-a6d944556973",
    },
    {
        id: "sales_skeptical_sme",
        name: "Skeptical SME Buyer",
        training_type: "sales",
        description: "A 10-person SME owner who has been burned before. Tests discovery and trust-building.",
        briefing:
            "You are a sales rep pitching a SaaS tool. The buyer runs a 10-person SME and has had bad experiences. Uncover needs before pitching features.",
    },
    {
        id: "sales_price_objector",
        name: "Price Objector",
        training_type: "sales",
        description: "A buyer who keeps pushing back on price. Tests value articulation and objection handling.",
        briefing:
            "You are a sales rep. The buyer likes the product but insists it's too expensive. Reframe around value and ROI, don't just discount.",
    },
    {
        id: "sales_cold_discovery",
        name: "Cold Discovery Call",
        training_type: "sales",
        description: "A lukewarm cold call. Tests opener, discovery questions, and next-step commitment.",
        briefing:
            "You are a sales rep making a first-touch call. The prospect is busy and skeptical. Earn 30 seconds, then uncover one real pain.",
    },
];

const STORAGE_KEY = "__training_mock_campaigns__";

const TRANSCRIPT_FIXTURES: Record<string, TranscriptTurn[]> = {
    cs_frustrated_customer: [
        { speaker: "persona", text: "Hello? My Pos Laju parcel was supposed to arrive 4 days ago. Where is it?", ts: "0:03" },
        { speaker: "employee", text: "I'm sorry to hear that. Let me pull up your tracking right now. May I have the consignment number?", ts: "0:09" },
        { speaker: "persona", text: "EX1234567MY. I needed it for my daughter's wedding yesterday. The whole family is upset.", ts: "0:18" },
        { speaker: "employee", text: "That's awful, I understand. I can see the parcel was flagged at the Shah Alam hub for sorting delay. I'm so sorry.", ts: "0:26" },
        { speaker: "persona", text: "Sorting delay? Nobody told me anything. Pos Laju supposed to be next-day, kan?", ts: "0:38" },
        { speaker: "employee", text: "You're right, we should have notified you. Let me make this right: I'll process a postage refund today and tag your parcel as priority for delivery first thing tomorrow.", ts: "0:42" },
        { speaker: "persona", text: "Tomorrow? I needed it yesterday lah.", ts: "0:55" },
        { speaker: "employee", text: "I hear you. Let me check if any branch nearby has it for self-collection today. One moment.", ts: "1:00" },
        { speaker: "persona", text: "Okay, please check.", ts: "1:08" },
        { speaker: "employee", text: "Good news — it's at our Subang branch right now. You can collect with your IC after 3pm today, or we'll deliver tomorrow morning. Which works for you?", ts: "1:14" },
        { speaker: "persona", text: "I'll go pick it up. At least one good thing today.", ts: "1:25" },
        { speaker: "employee", text: "Thank you for your patience. The postage refund will reflect in 3-5 working days. Anything else I can help with?", ts: "1:30" },
    ],
    cs_confused_elder: [
        { speaker: "persona", text: "Hello, I want to send this letter to my grandson. They said something about 'registered'?", ts: "0:04" },
        { speaker: "employee", text: "Of course, Aunty. Registered mail just means we track it and your grandson signs when he receives it. I'll walk you through it slowly. Where is he?", ts: "0:12" },
        { speaker: "persona", text: "In Penang. Is it expensive?", ts: "0:24" },
        { speaker: "employee", text: "For a small letter to Penang, registered is RM 4 on top of regular postage. Total around RM 6. Worth it for important documents.", ts: "0:30" },
        { speaker: "persona", text: "Okay. The form... I don't understand this part here.", ts: "0:42" },
        { speaker: "employee", text: "No worries, let me help. That section is for the sender — that's you. May I have your full name and IC number? I'll fill it in for you.", ts: "0:48" },
        { speaker: "persona", text: "Oh, you can do it for me? Thank you.", ts: "1:02" },
        { speaker: "employee", text: "Of course. And here for the recipient, your grandson — his name and address?", ts: "1:08" },
        { speaker: "persona", text: "Ahmad Razali, 14 Jalan Tanjung Tokong, Penang.", ts: "1:18" },
        { speaker: "employee", text: "Got it. I'll also give you a tracking slip — you can SMS this number to 33399 anytime to check where the letter is.", ts: "1:26" },
        { speaker: "persona", text: "Wah, modern. My grandson will be impressed.", ts: "1:38" },
        { speaker: "employee", text: "Take care, Aunty. He'll receive it in 2 working days. Thank you for using Pos Malaysia.", ts: "1:44" },
    ],
    cs_refund_demander: [
        { speaker: "persona", text: "Your tracking says my parcel was delivered 3 days ago. I never received it. I want full compensation.", ts: "0:02" },
        { speaker: "employee", text: "I'm sorry to hear that. Let me look into it immediately. Could you confirm the consignment number?", ts: "0:08" },
        { speaker: "persona", text: "EX9876543MY. The contents were RM 800. I want all of it back.", ts: "0:14" },
        { speaker: "employee", text: "I see the scan. The driver marked it delivered to your address. Did anyone in the household receive it on your behalf?", ts: "0:24" },
        { speaker: "persona", text: "Nobody. I checked everyone. CCTV also no parcel was dropped.", ts: "0:36" },
        { speaker: "employee", text: "Okay, that helps. I'll file a missing parcel claim right now. Can you share a photo of the CCTV timestamp? It strengthens the claim.", ts: "0:42" },
        { speaker: "persona", text: "Sure. But what about my RM 800?", ts: "0:55" },
        { speaker: "employee", text: "Honest answer: our standard liability covers up to RM 100 per parcel without insurance, and your parcel wasn't insured. I can escalate for a goodwill review, but I can't guarantee the full amount.", ts: "1:00" },
        { speaker: "persona", text: "RM 100? You're not serious.", ts: "1:14" },
        { speaker: "employee", text: "I know it's frustrating. What I can do: I'll personally escalate to our claims manager today, attach your CCTV evidence, and request an exception. Most cases like this get resolved within 7 working days.", ts: "1:18" },
        { speaker: "persona", text: "Fine. But I need updates, not silence.", ts: "1:32" },
        { speaker: "employee", text: "Agreed. I'll WhatsApp you a daily update until this is resolved. You'll have my direct line.", ts: "1:36" },
    ],
    sales_skeptical_sme: [
        { speaker: "persona", text: "Look, I've been burned before. Why should I trust your tool?", ts: "0:05" },
        { speaker: "employee", text: "Totally fair. Mind if I ask what didn't work last time?", ts: "0:11" },
        { speaker: "persona", text: "Promised the world, took 6 months to set up, no support.", ts: "0:18" },
        { speaker: "employee", text: "That's painful. We typically get small teams live in 4 days, and you'd have a dedicated CSM. Want me to walk you through how?", ts: "0:28" },
        { speaker: "persona", text: "Maybe. What's the catch?", ts: "0:41" },
        { speaker: "employee", text: "No catch on the timeline. The honest tradeoff: we don't customise UI for accounts under 50 seats. Is that a dealbreaker?", ts: "0:46" },
        { speaker: "persona", text: "Probably not. We just need it to work.", ts: "0:58" },
        { speaker: "employee", text: "Then let's set up a 20-minute pilot scoping call this week. I'll bring two reference customers your size who'll be honest about us.", ts: "1:03" },
    ],
    sales_price_objector: [
        { speaker: "persona", text: "Honestly, this is way too expensive.", ts: "0:03" },
        { speaker: "employee", text: "I hear that often. Can I ask what you're comparing it to?", ts: "0:08" },
        { speaker: "persona", text: "Tools that are half the price.", ts: "0:14" },
        { speaker: "employee", text: "Got it. Most of those don't include the AI scoring or the Asia-region compliance, which are the two things our customers tell us actually save them money.", ts: "0:18" },
        { speaker: "persona", text: "Save us money how?", ts: "0:32" },
        { speaker: "employee", text: "Customers cut HR coaching time by about 60%. For a team your size, that's roughly RM 8K/month freed up — paying for the tool 4x over.", ts: "0:36" },
        { speaker: "persona", text: "Hmm. I'd want to see those numbers in writing.", ts: "0:52" },
        { speaker: "employee", text: "Of course. I'll send you a 1-pager with two anonymous case studies. Worth a 15-min follow-up after you read it?", ts: "0:58" },
    ],
    sales_cold_discovery: [
        { speaker: "employee", text: "Hi, this is Sarah. I know you weren't expecting me — got 30 seconds?", ts: "0:01" },
        { speaker: "persona", text: "Make it quick.", ts: "0:06" },
        { speaker: "employee", text: "I'll be brief. We help support teams of 20-50 cut training time. Two questions: are you growing your team this year?", ts: "0:09" },
        { speaker: "persona", text: "Yes, hiring 8 people.", ts: "0:20" },
        { speaker: "employee", text: "How long does it take a new hire to feel confident on calls today?", ts: "0:24" },
        { speaker: "persona", text: "Six weeks, maybe more.", ts: "0:32" },
        { speaker: "employee", text: "We typically get that to 3 weeks. Worth a 20-min call next Tuesday or Wednesday to see if it'd work for your team?", ts: "0:38" },
        { speaker: "persona", text: "Tuesday could work. Send something through.", ts: "0:50" },
        { speaker: "employee", text: "Will do — calendar invite in your inbox in 5 minutes. Thanks for the time.", ts: "0:55" },
    ],
};

function transcriptForPersona(personaId: string): TranscriptTurn[] {
    return TRANSCRIPT_FIXTURES[personaId] ?? TRANSCRIPT_FIXTURES.cs_frustrated_customer;
}

const ROLE_DEPT_OPTIONS: Array<[string, string]> = [
    ["Support Rep", "Customer Success"],
    ["Senior Support", "Customer Success"],
    ["Team Lead", "Customer Success"],
    ["Account Executive", "Sales"],
    ["SDR", "Sales"],
    ["Sales Manager", "Sales"],
    ["Operations Specialist", "Operations"],
];

function loadCampaigns(): Record<string, TrainingCampaignDetail> {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        return JSON.parse(raw);
    } catch {
        return {};
    }
}

function saveCampaigns(data: Record<string, TrainingCampaignDetail>) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

async function loadCampaignsRemote(): Promise<Record<string, TrainingCampaignDetail>> {
    const list = await listCampaigns();
    const details = await Promise.all(list.map((c) => getCampaign(c.id)));
    const out: Record<string, TrainingCampaignDetail> = {};
    for (const d of details) {
        if (d) out[d.id] = d;
    }
    return out;
}

async function loadCampaignsAny(): Promise<Record<string, TrainingCampaignDetail>> {
    if (import.meta.env.VITE_USE_MOCK_TRAINING === "true") {
        // Merge real BE data with locally-seeded mock data so the dashboard
        // shows both. Real BE may fail in dev — degrade silently to mock-only.
        const remote = await loadCampaignsRemote().catch(() => ({}));
        return { ...remote, ...loadCampaigns() };
    }
    return loadCampaignsRemote();
}

function uid(prefix: string) {
    return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

function sleep(ms: number) {
    return new Promise((r) => setTimeout(r, ms));
}

function pickPersonaName(personaId: string): string {
    return PERSONAS.find((p) => p.id === personaId)?.name ?? personaId;
}

// Concise scenario blurb for a persona, for surfaces like the campaign table.
// Returns null when the persona isn't in the static catalog (e.g. BE-defined
// personas) so callers can fall back to a server-supplied description.
function pickPersonaDescription(personaId: string): string | null {
    return PERSONAS.find((p) => p.id === personaId)?.description ?? null;
}

function computeCampaignSummary(detail: TrainingCampaignDetail): TrainingCampaign {
    const realSessions = detail.sessions.filter((s) => !s.is_test);
    const completedEmails = new Set(
        realSessions.filter((s) => s.status === "completed").map((s) => s.employee_email)
    );
    const completed = realSessions.filter((s) => s.status === "completed");
    const totalScores = completed
        .map((s) => s.total_score)
        .filter((n): n is number => typeof n === "number");
    const avg =
        totalScores.length > 0
            ? Math.round((totalScores.reduce((a, b) => a + b, 0) / totalScores.length) * 10) / 10
            : null;
    const uniqueEmployees = new Set(realSessions.map((s) => s.employee_email)).size;
    return {
        id: detail.id,
        name: detail.name,
        training_type: detail.training_type,
        persona_id: detail.persona_id,
        status: detail.status,
        created_at: detail.created_at,
        employee_count: uniqueEmployees,
        completed_count: completedEmails.size,
        avg_score: avg,
        insights_headline: computeCampaignInsights(detail).headline,
    };
}

// Personas are presented alphabetically by name so the picker order is
// deterministic regardless of the order the BE (or mock list) returns them in.
function sortPersonasByName(personas: TrainingPersona[]): TrainingPersona[] {
    return [...personas].sort((a, b) => a.name.localeCompare(b.name));
}

export async function listPersonas(trainingType?: TrainingType): Promise<TrainingPersona[]> {
    if (USE_MOCK) {
        await sleep(80);
        const filtered = trainingType ? PERSONAS.filter((p) => p.training_type === trainingType) : PERSONAS;
        return sortPersonasByName(filtered);
    }
    try {
        const r = await axios.get(`${apiURL}/training/personas`, {
            headers: authHeaders(),
            params: trainingType ? { training_type: trainingType } : undefined,
            timeout: 10000,
        });
        const list = unwrap<TrainingPersona[]>(r.data) ?? [];
        const filtered = trainingType ? list.filter((p) => p.training_type === trainingType) : list;
        return sortPersonasByName(filtered);
    } catch {
        return [];
    }
}

export async function getPersona(personaId: string): Promise<TrainingPersona | null> {
    if (USE_MOCK) {
        await sleep(40);
        return PERSONAS.find((p) => p.id === personaId) ?? null;
    }
    // Call the individual endpoint first — it works for both static and custom
    // (EmployerAssessment-backed) personas and returns gender/language fields
    // that the list endpoint may omit for custom personas.
    try {
        const r = await axios.get(`${apiURL}/training/personas/${personaId}`, {
            headers: authHeaders(),
            timeout: 5000,
        });
        const raw = unwrap<Record<string, unknown>>(r.data) ?? {};
        if (raw.id) {
            return {
                id: String(raw.id),
                name: String(raw.name ?? ""),
                training_type: (raw.training_type ?? "cs") as TrainingType,
                description: String(raw.description ?? ""),
                briefing: String(raw.briefing ?? ""),
                assessment_id: raw.assessment_id ? String(raw.assessment_id) : undefined,
                voice_key: raw.voice_key ? String(raw.voice_key) : undefined,
                gender: (raw.gender as "male" | "female" | undefined) ?? undefined,
                language: raw.language != null ? String(raw.language) : undefined,
                max_duration_seconds: raw.max_duration_seconds != null ? Number(raw.max_duration_seconds) : null,
            };
        }
    } catch { /* fall through to list lookup */ }
    const list = await listPersonas();
    return list.find((p) => p.id === personaId) ?? null;
}

export async function listCampaigns(): Promise<TrainingCampaign[]> {
    let real: TrainingCampaign[] = [];
    try {
        const r = await axios.get(`${apiURL}/training/campaigns`, {
            headers: authHeaders(),
            timeout: 10000,
        });
        real = unwrap<TrainingCampaign[]>(r.data) ?? [];
    } catch {
        real = [];
    }
    if (USE_MOCK) {
        await sleep(100);
        const mockSummaries = Object.values(loadCampaigns()).map(computeCampaignSummary);
        return [...real, ...mockSummaries].sort((a, b) =>
            b.created_at.localeCompare(a.created_at)
        );
    }
    return real.sort((a, b) => b.created_at.localeCompare(a.created_at));
}

export async function getCampaign(id: string): Promise<TrainingCampaignDetail | null> {
    if (USE_MOCK) {
        await sleep(100);
        return loadCampaigns()[id] ?? null;
    }
    try {
        const r = await axios.get(`${apiURL}/training/campaigns/${id}`, {
            headers: authHeaders(),
            timeout: 10000,
        });
        return unwrap<TrainingCampaignDetail>(r.data) ?? null;
    } catch {
        return null;
    }
}

// Lightweight aggregate metrics for the campaign header cards
// (GET /training/campaigns/{id}/stats). Status filter rule: only `completed`
// (non-test) sessions count toward total_completed / avg_score / total_attempts;
// total_employees is the full roster (distinct employees across any status).
export interface CampaignStats {
    total_employees: number;
    total_completed: number;
    avg_score: number | null;
    total_attempts: number;
}

export async function getCampaignStats(
    campaignId: string,
): Promise<CampaignStats | null> {
    if (USE_MOCK) {
        const detail = await getCampaign(campaignId);
        if (!detail) return null;
        const real = detail.sessions.filter((s) => !s.is_test);
        const completed = real.filter((s) => s.status === "completed");
        const scored = completed
            .map((s) => s.total_score)
            .filter((n): n is number => typeof n === "number");
        const avg =
            scored.length > 0
                ? Math.round((scored.reduce((a, b) => a + b, 0) / scored.length) * 10) / 10
                : null;
        return {
            total_employees: new Set(real.map((s) => s.employee_email)).size,
            total_completed: new Set(completed.map((s) => s.employee_email)).size,
            avg_score: avg,
            total_attempts: completed.length,
        };
    }
    try {
        const r = await axios.get(
            `${apiURL}/training/campaigns/${campaignId}/stats`,
            { headers: authHeaders(), timeout: 10000 },
        );
        return unwrap<CampaignStats>(r.data) ?? null;
    } catch {
        return null;
    }
}

export async function updateCampaignStatus(id: string, status: CampaignStatus): Promise<TrainingCampaign | null> {
    if (USE_MOCK) {
        await sleep(80);
        const all = loadCampaigns();
        if (!all[id]) return null;
        all[id].status = status;
        saveCampaigns(all);
        return computeCampaignSummary(all[id]);
    }
    try {
        const r = await axios.patch(
            `${apiURL}/training/campaigns/${id}/status`,
            { status },
            { headers: authHeaders(), timeout: 10000 }
        );
        return unwrap<TrainingCampaign>(r.data) ?? null;
    } catch {
        return null;
    }
}

// Publish a draft campaign — flips status to "active". On the backend this
// is the trigger that sends the (until-now withheld) trainee invitations.
// Thin semantic alias over updateCampaignStatus so call-sites read clearly.
export async function publishCampaign(id: string): Promise<TrainingCampaign | null> {
    return updateCampaignStatus(id, "active");
}

export interface UpdateCampaignInput {
    name: string;
    team_leader?: string | null;
    // Optional invitation email overrides — when provided, the backend
    // stores them on the campaign and uses them when sending invitations.
    // `email_body` is HTML (matches what the rich-text editor produces).
    email_subject?: string;
    email_body?: string;
}

// PUT /training/campaigns/{campaign_id} — updates editable campaign fields.
// Per the API contract `name` is required (non-empty / non-whitespace);
// scoring-weight changes go through updateRubric instead. Returns the
// updated campaign so callers can refresh without a re-fetch.
export async function updateCampaign(
    id: string,
    input: UpdateCampaignInput,
): Promise<TrainingCampaign | void> {
    if (USE_MOCK) {
        await sleep(80);
        const all = loadCampaigns();
        if (all[id]) {
            all[id].name = input.name;
            saveCampaigns(all);
        }
        return;
    }
    const body: Record<string, unknown> = { name: input.name.trim() };
    if (input.email_subject !== undefined) body.email_subject = input.email_subject;
    if (input.email_body !== undefined) body.email_body = input.email_body;
    const r = await axios.put(
        `${apiURL}/training/campaigns/${id}`,
        body,
        { headers: authHeaders(), timeout: 10000 }
    );
    return unwrap<TrainingCampaign>(r.data);
}

/**
 * @deprecated Use `updateCampaign` instead. Kept as a thin compatibility
 * wrapper for callers that only need to change the name.
 */
export async function renameCampaign(id: string, name: string): Promise<TrainingCampaign | void> {
    return updateCampaign(id, { name });
}

export interface CreateCampaignInput {
    name: string;
    training_type: TrainingType;
    persona_id: string;
    employees: EmployeeRow[];
    team_leader?: string | null;
    // Initial lifecycle status. "draft" creates a pre-launch campaign that
    // does NOT send invitations yet (publish later to go live); "active"
    // launches immediately. Defaults to "active" to preserve prior behavior.
    status?: CampaignStatus;
    // Optional invitation email overrides — when omitted the backend uses
    // its default template. `email_body` is HTML (rich-text editor output).
    email_subject?: string;
    email_body?: string;
}

export async function createCampaign(input: CreateCampaignInput): Promise<TrainingCampaign> {
    if (USE_MOCK) {
        await sleep(200);
        const all = loadCampaigns();
        const id = uid("camp");
        const nowIso = new Date().toISOString();
        const sessions: TrainingSession[] = input.employees.map((e) => ({
            id: uid("sess"),
            campaign_id: id,
            employee_email: e.email,
            employee_name: e.name,
            employee_role: e.role ?? null,
            employee_department: e.department ?? null,
            token: uid("tok"),
            attempt_number: 1,
            status: "invited",
            started_at: null,
            completed_at: null,
            rubric_scores: null,
            total_score: null,
            qualitative_feedback: null,
        }));
        const detail: TrainingCampaignDetail = {
            id,
            name: input.name,
            training_type: input.training_type,
            persona_id: input.persona_id,
            status: input.status ?? "active",
            created_at: nowIso,
            employee_count: sessions.length,
            completed_count: 0,
            avg_score: null,
            sessions,
        };
        all[id] = detail;
        saveCampaigns(all);
        return computeCampaignSummary(detail);
    }
    const body: Record<string, unknown> = {
        name: input.name,
        training_type: input.training_type,
        persona_id: input.persona_id,
        employees: input.employees,
        status: input.status ?? "active",
    };
    if (input.email_subject !== undefined) body.email_subject = input.email_subject;
    if (input.email_body !== undefined) body.email_body = input.email_body;
    const r = await axios.post(
        `${apiURL}/training/campaigns`,
        body,
        { headers: authHeaders(), timeout: 30000 }
    );
    return unwrap<TrainingCampaign>(r.data);
}

export interface AddEmployeesResult {
    campaign: TrainingCampaign;
    added: number;
    skipped_duplicates: number;
}

/**
 * Add more employees to an existing campaign after it's been created.
 *
 * Mock mode: appends new TrainingSession rows to the campaign in localStorage,
 * dedupes by employee_email, mints fresh tokens.
 *
 * Real mode: POST /training/campaigns/{id}/employees. Endpoint is pending on the
 * BE — see docs/TRAINING_EXPERIENCE_BE_2026-04-29.md. Returns null on failure.
 */
export async function addEmployeesToCampaign(
    campaignId: string,
    employees: EmployeeRow[]
): Promise<AddEmployeesResult | null> {
    if (USE_MOCK) {
        await sleep(250);
        const all = loadCampaigns();
        const detail = all[campaignId];
        if (!detail) return null;
        const existingEmails = new Set(
            detail.sessions.map((s) => s.employee_email.toLowerCase())
        );
        const fresh: TrainingSession[] = [];
        let skipped = 0;
        for (const e of employees) {
            const key = e.email.trim().toLowerCase();
            if (!key || existingEmails.has(key)) {
                skipped += 1;
                continue;
            }
            existingEmails.add(key);
            fresh.push({
                id: uid("sess"),
                campaign_id: campaignId,
                employee_email: e.email,
                employee_name: e.name,
                employee_role: e.role ?? null,
                employee_department: e.department ?? null,
                token: uid("tok"),
                attempt_number: 1,
                status: "invited",
                started_at: null,
                completed_at: null,
                rubric_scores: null,
                total_score: null,
                qualitative_feedback: null,
            });
        }
        const updated: TrainingCampaignDetail = {
            ...detail,
            sessions: [...detail.sessions, ...fresh],
            employee_count: detail.employee_count + fresh.length,
        };
        all[campaignId] = updated;
        saveCampaigns(all);
        return {
            campaign: computeCampaignSummary(updated),
            added: fresh.length,
            skipped_duplicates: skipped,
        };
    }
    try {
        const r = await axios.post(
            `${apiURL}/training/campaigns/${campaignId}/employees`,
            { employees },
            { headers: authHeaders(), timeout: 30000 }
        );
        return unwrap<AddEmployeesResult>(r.data);
    } catch {
        return null;
    }
}

export async function getSessionByToken(token: string): Promise<{
    session: TrainingSession;
    campaign: TrainingCampaign;
    persona: TrainingPersona;
    rubric_criteria?: RubricCriterion[];
} | null> {
    if (USE_MOCK) {
        await sleep(80);
        const all = loadCampaigns();
        for (const c of Object.values(all)) {
            const s = c.sessions.find((x) => x.token === token);
            if (s) {
                const persona = PERSONAS.find((p) => p.id === c.persona_id);
                if (!persona) return null;
                return { session: s, campaign: computeCampaignSummary(c), persona };
            }
        }
        return null;
    }
    try {
        const r = await axios.get(`${apiURL}/training/session/${token}`, {
            headers: publicHeaders(),
            timeout: 10000,
        });
        return unwrap<{ session: TrainingSession; campaign: TrainingCampaign; persona: TrainingPersona; rubric_criteria?: RubricCriterion[] }>(r.data) ?? null;
    } catch {
        return null;
    }
}

function buildFeedback(scores: RubricScores, total: number): {
    summary: string;
    strengths: string;
    growth: string;
    highlight: { timestamp: string; quote: string; coach_note: string };
} {
    const sorted = (Object.entries(scores) as [keyof RubricScores, number][]).sort(
        (a, b) => b[1] - a[1]
    );
    const top = sorted[0][0];
    const bottom = sorted[sorted.length - 1][0];

    const strengthsByDim: Record<keyof RubricScores, string> = {
        empathy: "You acknowledged the customer's frustration before pivoting to the fix.",
        problem_resolution: "You moved quickly to a concrete resolution the customer could act on.",
        product_knowledge: "You answered product questions confidently and accurately.",
        tone: "Your tone stayed calm and professional even when the customer pushed back.",
        call_handling: "You structured the call well: opener, discovery, summary, next step.",
    };
    const growthByDim: Record<keyof RubricScores, string> = {
        empathy: "When the customer escalated, slow down and name the emotion before offering a solution.",
        problem_resolution: "Commit to a single clear next step instead of listing options the customer has to choose between.",
        product_knowledge: "Two product questions caught you off-guard. Review the FAQ doc on refund policy edge cases.",
        tone: "You went a bit defensive at the 3-minute mark. Try a confirming phrase like 'I hear you, that's fair' to soften.",
        call_handling: "End with a clear summary of who does what by when. Don't leave the next step implicit.",
    };

    const highlightsByDim: Record<keyof RubricScores, { quote: string; coach_note: string }> = {
        empathy: {
            quote: "Customer: \"This is the third time I've called about this!\"",
            coach_note: "You jumped to the fix here. Try acknowledging first: 'I'm sorry you've had to call three times. That's not the experience we want for you.'",
        },
        problem_resolution: {
            quote: "You: \"We could either refund, or replace, or send a credit...\"",
            coach_note: "Pick one and recommend it. Customers want a guide, not a menu.",
        },
        product_knowledge: {
            quote: "Customer: \"What's your refund window for damaged items?\"",
            coach_note: "You hesitated for 6 seconds. Damaged items have a 60-day window — keep the policy doc open during calls.",
        },
        tone: {
            quote: "You: \"Well, the policy clearly states...\"",
            coach_note: "Avoid 'clearly' and 'obviously' on calls. They land as condescending. Try 'our policy is...' instead.",
        },
        call_handling: {
            quote: "End of call",
            coach_note: "You ended with 'we'll be in touch'. Always specify: 'I'll email you a confirmation in the next 10 minutes.'",
        },
    };

    const summary =
        total >= 80
            ? "Strong session. The customer would have walked away satisfied."
            : total >= 60
            ? "Solid attempt with room to sharpen one or two habits."
            : "Tough one. Focus on the fundamentals below before the next attempt.";

    const minutes = 2 + Math.floor(Math.random() * 4);
    const seconds = Math.floor(Math.random() * 60);
    const timestamp = `${minutes}:${seconds.toString().padStart(2, "0")}`;

    return {
        summary,
        strengths: strengthsByDim[top],
        growth: growthByDim[bottom],
        highlight: {
            timestamp,
            quote: highlightsByDim[bottom].quote,
            coach_note: highlightsByDim[bottom].coach_note,
        },
    };
}

export async function retrySession(token: string): Promise<TrainingSession | null> {
    if (USE_MOCK) {
        await sleep(200);
        const all = loadCampaigns();
        for (const c of Object.values(all)) {
            const existing = c.sessions.filter((x) => x.employee_email && x.token === token)[0];
            if (!existing) continue;
            const highestAttempt = Math.max(
                ...c.sessions
                    .filter((x) => x.employee_email === existing.employee_email)
                    .map((x) => x.attempt_number)
            );
            const fresh: TrainingSession = {
                id: uid("sess"),
                campaign_id: c.id,
                employee_email: existing.employee_email,
                employee_name: existing.employee_name,
                token: uid("tok"),
                attempt_number: highestAttempt + 1,
                status: "invited",
                started_at: null,
                completed_at: null,
                rubric_scores: null,
                total_score: null,
                qualitative_feedback: null,
            };
            c.sessions.push(fresh);
            saveCampaigns(all);
            return fresh;
        }
        return null;
    }
    try {
        const r = await axios.post(`${apiURL}/training/sessions/${token}/retry`, null, {
            headers: publicHeaders(),
            timeout: 10000,
        });
        return unwrap<TrainingSession>(r.data) ?? null;
    } catch (err) {
        catchErrorLog(`[training] retrySession failed for token ${token}: ${err instanceof Error ? err.message : String(err)}`);
        return null;
    }
}

export function parseCsv(text: string): { rows: EmployeeRow[]; errors: string[] } {
    const errors: string[] = [];
    const rows: EmployeeRow[] = [];
    // Strip UTF-8 BOM (added by Excel/Numbers when saving as CSV)
    const cleaned = text.replace(/^\uFEFF/, "");
    // Detect delimiter: comma, semicolon, or tab
    const firstLine = cleaned.split(/\r?\n/)[0] ?? "";
    const delimiter = firstLine.includes(";") && !firstLine.includes(",")
        ? ";"
        : firstLine.includes("\t") && !firstLine.includes(",")
        ? "\t"
        : ",";
    const stripField = (s: string) =>
        s.replace(/^\uFEFF/, "").trim().replace(/^"(.*)"$/, "$1").trim();
    const lines = cleaned
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);
    if (lines.length === 0) {
        errors.push("CSV is empty");
        return { rows, errors };
    }
    const header = lines[0].split(delimiter).map((h) => stripField(h).toLowerCase());
    const nameIdx = header.indexOf("name");
    const emailIdx = header.indexOf("email");
    const roleIdx = header.indexOf("role");
    if (nameIdx === -1 || emailIdx === -1) {
        const looksLikeData = header.some((h) => h.includes("@"));
        if (looksLikeData) {
            errors.push(
                `Your CSV is missing a header row. The first line must declare column names: "name,email" (with an optional "role"). Put your employees on the lines below.`
            );
        } else {
            errors.push(
                `CSV must include columns: name, email. Found columns: ${header.join(", ") || "(none)"}.`
            );
        }
        return { rows, errors };
    }
    const seenEmails = new Set<string>();
    for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(delimiter).map(stripField);
        const name = cols[nameIdx];
        const email = cols[emailIdx];
        const role = roleIdx >= 0 ? cols[roleIdx] : undefined;
        if (!name || !email) {
            errors.push(`Row ${i + 1}: missing name or email`);
            continue;
        }
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
            errors.push(`Row ${i + 1}: invalid email "${email}"`);
            continue;
        }
        if (seenEmails.has(email.toLowerCase())) continue;
        seenEmails.add(email.toLowerCase());
        rows.push({ name, email, role });
    }
    if (rows.length > 500) {
        errors.push(`CSV exceeds 500-employee cap (found ${rows.length})`);
    }
    return { rows, errors };
}

export { PERSONAS, pickPersonaName, pickPersonaDescription, ROLE_DEPT_OPTIONS, transcriptForPersona };

export interface PersonRow {
    name: string;
    email: string;
    role: string | null;
    department: string | null;
    latest_session: TrainingSession;
    attempts: TrainingSession[];
    campaigns: Array<{ id: string; name: string }>;
    needs_attention: boolean;
    needs_attention_reason?: string;
}

export async function listAllPeople(): Promise<PersonRow[]> {
    const all = await loadCampaignsAny();
    const byEmail = new Map<string, PersonRow>();
    for (const c of Object.values(all)) {
        for (const s of c.sessions) {
            if (s.is_test) continue;
            const existing = byEmail.get(s.employee_email);
            if (!existing) {
                byEmail.set(s.employee_email, {
                    name: s.employee_name,
                    email: s.employee_email,
                    role: s.employee_role ?? null,
                    department: s.employee_department ?? null,
                    latest_session: s,
                    attempts: [s],
                    campaigns: [{ id: c.id, name: c.name }],
                    needs_attention: false,
                });
            } else {
                existing.attempts.push(s);
                if (!existing.campaigns.find((x) => x.id === c.id)) {
                    existing.campaigns.push({ id: c.id, name: c.name });
                }
                if (s.attempt_number > existing.latest_session.attempt_number) {
                    existing.latest_session = s;
                }
            }
        }
    }
    const rows = Array.from(byEmail.values());
    const now = Date.now();
    for (const p of rows) {
        p.attempts.sort((a, b) => a.attempt_number - b.attempt_number);
        const scored = p.attempts.filter((a) => a.total_score !== null);
        if (scored.length >= 2) {
            const last = scored[scored.length - 1].total_score!;
            const prev = scored[scored.length - 2].total_score!;
            if (last - prev <= -2) {
                p.needs_attention = true;
                p.needs_attention_reason = `Score dropped ${prev - last} pts`;
            }
        }
        if (
            p.latest_session.status === "invited" &&
            now - new Date(p.latest_session.started_at ?? new Date()).getTime() > 7 * 24 * 60 * 60 * 1000
        ) {
            // Skip — attempt-based check below covers this
        }
        if (p.latest_session.status === "invited" && p.attempts.every((a) => a.status === "invited")) {
            p.needs_attention = true;
            p.needs_attention_reason = p.needs_attention_reason ?? "No attempt yet";
        }
    }
    return rows.sort((a, b) => {
        if (a.needs_attention !== b.needs_attention) return a.needs_attention ? -1 : 1;
        return a.name.localeCompare(b.name);
    });
}

export async function createTestSession(campaignId: string): Promise<string | null> {
    if (USE_MOCK) {
        await sleep(150);
        const all = loadCampaigns();
        const campaign = all[campaignId];
        if (!campaign) return null;
        const token = uid("test");
        const session: TrainingSession = {
            id: uid("sess"),
            campaign_id: campaignId,
            employee_email: "test@ourteam.demo",
            employee_name: "Test User (you)",
            employee_role: "Test",
            employee_department: null,
            token,
            attempt_number: 1,
            status: "invited",
            started_at: null,
            completed_at: null,
            rubric_scores: null,
            total_score: null,
            qualitative_feedback: null,
            is_test: true,
        };
        campaign.sessions.push(session);
        saveCampaigns(all);
        return token;
    }
    try {
        const r = await axios.post(
            `${apiURL}/training/campaigns/${campaignId}/test-session`,
            {},
            { headers: authHeaders(), timeout: 10000 }
        );
        const data = unwrap<{ token: string; session_id?: string }>(r.data);
        return data?.token ?? null;
    } catch {
        return null;
    }
}

export function getEmployeeAttemptsInCampaign(
    detail: TrainingCampaignDetail,
    email: string
): TrainingSession[] {
    return detail.sessions
        .filter((s) => s.employee_email === email)
        .sort((a, b) => a.attempt_number - b.attempt_number);
}

export interface ActivityEvent {
    id: string;
    ts: string;
    type:
        | "campaign_created"
        | "invite_sent"
        | "session_started"
        | "session_completed"
        | "session_abandoned"
        | "retry_started";
    employee_name?: string;
    detail?: string;
    performed_by?: string;
}

export interface TopPerformer {
    name: string;
    email: string;
    role: string | null;
    department: string | null;
    score: number;
    campaign_name: string;
    persona_name: string;
    // Campaign the employee belongs to — used to deep-link from the dashboard
    // to the campaign detail with the employee pre-filtered. Optional until the
    // BE includes it in the dashboard payloads (see BE handoff).
    campaign_id?: string;
}

export interface BiggestImprover {
    name: string;
    email: string;
    role: string | null;
    department: string | null;
    first_score: number;
    latest_score: number;
    delta: number;
    attempts: number;
    campaign_id?: string;
}

// Dashboard card — GET /training/dashboard/top-performers?limit=5.
export async function getTopPerformers(limit = 5): Promise<TopPerformer[]> {
    if (USE_MOCK) {
        const all = await loadCampaignsAny();
        const out: TopPerformer[] = [];
        for (const c of Object.values(all)) {
            for (const s of c.sessions) {
                if (s.is_test) continue;
                if (s.status === "completed" && s.total_score !== null) {
                    out.push({
                        name: s.employee_name,
                        email: s.employee_email,
                        role: s.employee_role ?? null,
                        department: s.employee_department ?? null,
                        score: s.total_score,
                        campaign_name: c.name,
                        campaign_id: c.id,
                        // Prefer BE-supplied persona name; fall back to the local
                        // lookup (which itself returns the raw id when unknown).
                        persona_name:
                            s.persona_name ?? c.persona_name ?? pickPersonaName(c.persona_id),
                    });
                }
            }
        }
        return out.sort((a, b) => b.score - a.score).slice(0, limit);
    }
    try {
        const r = await axios.get(
            `${apiURL}/training/dashboard/top-performers?limit=${limit}`,
            { headers: authHeaders(), timeout: 10000 },
        );
        // BE returns employee_* field names (per contract); map to the FE shape,
        // tolerating either naming.
        const raw = unwrap<Array<Record<string, unknown>>>(r.data) ?? [];
        return raw.map((x) => ({
            name: (x.employee_name ?? x.name ?? "") as string,
            email: (x.employee_email ?? x.email ?? "") as string,
            role: (x.employee_role ?? x.role ?? null) as string | null,
            department: (x.employee_department ?? x.department ?? null) as string | null,
            score: (x.score ?? 0) as number,
            campaign_name: (x.campaign_name ?? "") as string,
            campaign_id: (x.campaign_id ?? x.employee_campaign_id) as string | undefined,
            persona_name: (x.persona_name ?? "") as string,
        }));
    } catch {
        return [];
    }
}

// Dashboard card — GET /training/dashboard/biggest-improvers?limit=4.
export async function getBiggestImprovers(limit = 5): Promise<BiggestImprover[]> {
    if (USE_MOCK) {
        await sleep(50);
        const people = await listAllPeople();
        const improvers: BiggestImprover[] = [];
        for (const p of people) {
            const scored = p.attempts.filter((a) => a.total_score !== null);
            if (scored.length < 2) continue;
            const first = scored[0].total_score!;
            const latest = scored[scored.length - 1].total_score!;
            const delta = latest - first;
            if (delta <= 0) continue;
            improvers.push({
                name: p.name,
                email: p.email,
                role: p.role,
                department: p.department,
                first_score: first,
                latest_score: latest,
                delta,
                attempts: scored.length,
            });
        }
        return improvers.sort((a, b) => b.delta - a.delta).slice(0, limit);
    }
    try {
        const r = await axios.get(
            `${apiURL}/training/dashboard/biggest-improvers?limit=${limit}`,
            { headers: authHeaders(), timeout: 10000 },
        );
        // BE returns employee_* field names (per contract); map to the FE shape,
        // tolerating either naming.
        const raw = unwrap<Array<Record<string, unknown>>>(r.data) ?? [];
        return raw.map((x) => ({
            name: (x.employee_name ?? x.name ?? "") as string,
            email: (x.employee_email ?? x.email ?? "") as string,
            role: (x.employee_role ?? x.role ?? null) as string | null,
            department: (x.employee_department ?? x.department ?? null) as string | null,
            first_score: (x.first_score ?? 0) as number,
            latest_score: (x.latest_score ?? 0) as number,
            delta: (x.delta ?? 0) as number,
            attempts: (x.attempts ?? 0) as number,
        }));
    } catch {
        return [];
    }
}

// Dashboard card — GET /training/dashboard/todays-actions?limit=4. Employees
// who need attention (score dropped, or invited but never started).
export interface TodaysActionItem {
    employee_name: string;
    employee_email: string;
    status: SessionStatus;
    reason: string;
    campaign_id?: string;
}

export interface TodaysActionsResult {
    total: number;
    items: TodaysActionItem[];
}

export async function getTodaysActions(limit = 4): Promise<TodaysActionsResult> {
    if (USE_MOCK) {
        const people = await listAllPeople();
        const needing = people.filter((p) => p.needs_attention);
        return {
            total: needing.length,
            items: needing.slice(0, limit).map((p) => ({
                employee_name: p.name,
                employee_email: p.email,
                status: p.latest_session.status,
                reason: p.needs_attention_reason ?? "Needs attention",
            })),
        };
    }
    try {
        const r = await axios.get(
            `${apiURL}/training/dashboard/todays-actions?limit=${limit}`,
            { headers: authHeaders(), timeout: 10000 },
        );
        return unwrap<TodaysActionsResult>(r.data) ?? { total: 0, items: [] };
    } catch {
        return { total: 0, items: [] };
    }
}

export interface DashboardActivityItem extends ActivityEvent {
    campaign_id: string;
    campaign_name: string;
}

// Dashboard card — GET /training/dashboard/recent-activity?limit=10.
export async function getRecentActivity(limit = 10): Promise<DashboardActivityItem[]> {
    if (USE_MOCK) {
        const all = await loadCampaignsAny();
        const events: DashboardActivityItem[] = [];
        for (const c of Object.values(all)) {
            for (const e of deriveActivityLog(c)) {
                events.push({ ...e, campaign_id: c.id, campaign_name: c.name });
            }
        }
        return events.sort((a, b) => b.ts.localeCompare(a.ts)).slice(0, limit);
    }
    try {
        const r = await axios.get(
            `${apiURL}/training/dashboard/recent-activity?limit=${limit}`,
            { headers: authHeaders(), timeout: 10000 },
        );
        return unwrap<DashboardActivityItem[]>(r.data) ?? [];
    } catch {
        return [];
    }
}

// Dashboard header KPI cards — GET /training/dashboard/summary.
// Uses the consistent definitions (matching /reports/*-summary): total_completed
// = distinct employees with >=1 completed session; avg_score = per-session mean.
export interface DashboardSummary {
    active_campaigns: number;
    people_enrolled: number;
    total_completed: number;
    completion_rate: number;
    avg_score: number | null;
}

export async function getDashboardSummary(): Promise<DashboardSummary | null> {
    if (USE_MOCK) {
        const all = await loadCampaignsAny();
        const campaignList = Object.values(all);
        const active_campaigns = campaignList.filter((c) => c.status === "active").length;
        const realSessions: TrainingSession[] = [];
        for (const c of campaignList) {
            for (const s of c.sessions) {
                if (!s.is_test) realSessions.push(s);
            }
        }
        const people_enrolled = new Set(realSessions.map((s) => s.employee_email)).size;
        const total_completed = new Set(
            realSessions
                .filter((s) => s.status === "completed")
                .map((s) => s.employee_email),
        ).size;
        const scored = realSessions
            .filter((s) => s.status === "completed" && s.total_score !== null)
            .map((s) => s.total_score as number);
        const avg_score =
            scored.length > 0
                ? Math.round((scored.reduce((a, b) => a + b, 0) / scored.length) * 10) / 10
                : null;
        const completion_rate =
            people_enrolled > 0
                ? Math.round((total_completed / people_enrolled) * 100)
                : 0;
        return { active_campaigns, people_enrolled, total_completed, completion_rate, avg_score };
    }
    try {
        const r = await axios.get(`${apiURL}/training/dashboard/summary`, {
            headers: authHeaders(),
            timeout: 10000,
        });
        return unwrap<DashboardSummary>(r.data) ?? null;
    } catch {
        return null;
    }
}

// Dashboard "Recent campaigns" list — GET /training/dashboard/recent-campaigns.
// Returns the most recent campaigns with the persona DISPLAY NAME (not the UUID).
export interface RecentCampaign {
    id: string;
    name: string;
    training_type: TrainingType;
    persona_name: string;
    employee_count: number;
    completed_count: number;
    avg_score: number | null;
    status?: CampaignStatus | null;
    team_leader?: string | null;
}

export async function getRecentCampaigns(limit = 5): Promise<RecentCampaign[]> {
    if (USE_MOCK) {
        const all = await loadCampaignsAny();
        return Object.values(all)
            .slice()
            .sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""))
            .slice(0, limit)
            .map((c) => {
                const real = c.sessions.filter((s) => !s.is_test);
                const employee_count = new Set(real.map((s) => s.employee_email)).size;
                const completed_count = new Set(
                    real.filter((s) => s.status === "completed").map((s) => s.employee_email),
                ).size;
                const scored = real
                    .filter((s) => s.status === "completed" && s.total_score !== null)
                    .map((s) => s.total_score as number);
                const avg_score = scored.length
                    ? Math.round((scored.reduce((x, y) => x + y, 0) / scored.length) * 10) / 10
                    : null;
                return {
                    id: c.id,
                    name: c.name,
                    training_type: c.training_type,
                    // Persona display name, never the UUID.
                    persona_name: c.persona_name ?? pickPersonaName(c.persona_id),
                    employee_count,
                    completed_count,
                    avg_score,
                };
            });
    }
    try {
        const r = await axios.get(
            `${apiURL}/training/dashboard/recent-campaigns?limit=${limit}`,
            { headers: authHeaders(), timeout: 10000 },
        );
        return unwrap<RecentCampaign[]>(r.data) ?? [];
    } catch {
        return [];
    }
}

// --- Reports helpers ---

export interface ScoreBucket {
    range: string;
    min: number;
    max: number;
    count: number;
}

export interface CompletionPoint {
    date: string;
    label: string;
    completed: number;
}

export interface PersonaEffectiveness {
    persona_id: string;
    persona_name: string;
    avg_score: number;
    sessions_count: number;
}

export interface DepartmentRow {
    department: string;
    avg_score: number;
    completed_count: number;
    people_count: number;
}

export interface ReportsData {
    score_buckets: ScoreBucket[];
    completion_trend: CompletionPoint[];
    persona_effectiveness: PersonaEffectiveness[];
    department_leaderboard: DepartmentRow[];
    total_completed: number;
    overall_avg_score: number | null;
}

export async function getReportsData(): Promise<ReportsData> {
    const all = await loadCampaignsAny();
    const completedSessions: TrainingSession[] = [];
    for (const c of Object.values(all)) {
        for (const s of c.sessions) {
            if (s.is_test) continue;
            if (s.status === "completed" && s.total_score !== null) {
                completedSessions.push(s);
            }
        }
    }

    // Score distribution: 5 buckets of width 20 (0-20, 21-40, 41-60, 61-80, 81-100)
    const bucketDefs = [
        { range: "0–20", min: 0, max: 20 },
        { range: "21–40", min: 21, max: 40 },
        { range: "41–60", min: 41, max: 60 },
        { range: "61–80", min: 61, max: 80 },
        { range: "81–100", min: 81, max: 100 },
    ];
    const score_buckets: ScoreBucket[] = bucketDefs.map((b) => ({
        ...b,
        count: completedSessions.filter((s) => s.total_score! >= b.min && s.total_score! <= b.max).length,
    }));

    // Completion trend over last 30 days
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const completion_trend: CompletionPoint[] = [];
    for (let i = 29; i >= 0; i--) {
        const day = new Date(today.getTime() - i * 24 * 60 * 60 * 1000);
        const dayStart = day.getTime();
        const dayEnd = dayStart + 24 * 60 * 60 * 1000;
        const count = completedSessions.filter((s) => {
            if (!s.completed_at) return false;
            const t = new Date(s.completed_at).getTime();
            return t >= dayStart && t < dayEnd;
        }).length;
        completion_trend.push({
            date: day.toISOString().slice(0, 10),
            label: day.toLocaleDateString("en-MY", { month: "short", day: "numeric" }),
            completed: count,
        });
    }

    // Persona effectiveness. Prefer the campaign's BE-supplied persona_name
    // (e.g. "GreenSM Late Pick-Up") over pickPersonaName(persona_id), which
    // returns the raw UUID for personas not in the curated FE table — that UUID
    // then renders as "Unknown persona" in the reports card.
    const byPersona = new Map<
        string,
        { sum: number; count: number; name: string }
    >();
    for (const c of Object.values(all)) {
        const campaignPersonaName = (c.persona_name ?? "").trim();
        for (const s of c.sessions) {
            if (s.is_test) continue;
            if (s.status === "completed" && s.total_score !== null) {
                const cur = byPersona.get(c.persona_id) ?? {
                    sum: 0,
                    count: 0,
                    name: campaignPersonaName || pickPersonaName(c.persona_id),
                };
                cur.sum += s.total_score;
                cur.count += 1;
                // Upgrade to a real name if a later campaign for the same
                // persona has one and the current stored name is empty/UUID.
                if (campaignPersonaName && !cur.name) cur.name = campaignPersonaName;
                byPersona.set(c.persona_id, cur);
            }
        }
    }
    const persona_effectiveness: PersonaEffectiveness[] = Array.from(byPersona.entries())
        .map(([persona_id, v]) => ({
            persona_id,
            persona_name: v.name,
            avg_score: Math.round((v.sum / v.count) * 10) / 10,
            sessions_count: v.count,
        }))
        .sort((a, b) => b.avg_score - a.avg_score);

    // Department leaderboard. Normalize the department key (trim + collapse
    // whitespace + case-insensitive) so "Customer Service", "customer service"
    // and "Customer  Service" collapse into a single row instead of separate
    // ones. Genuinely different labels like "CS" vs "Customer Service" stay
    // separate — we don't guess aliases. NOTE: only COMPLETED sessions are
    // counted here, so a department whose employees haven't completed any
    // session will not appear at all.
    const byDept = new Map<string, { label: string; sum: number; count: number; emails: Set<string> }>();
    for (const s of completedSessions) {
        if (!s.employee_department) continue;
        const label = s.employee_department.trim().replace(/\s+/g, " ");
        if (!label) continue;
        const key = label.toLowerCase();
        const cur = byDept.get(key) ?? { label, sum: 0, count: 0, emails: new Set() };
        cur.sum += s.total_score!;
        cur.count += 1;
        cur.emails.add(s.employee_email);
        byDept.set(key, cur);
    }
    const department_leaderboard: DepartmentRow[] = Array.from(byDept.values())
        .map((v) => ({
            department: v.label,
            avg_score: Math.round((v.sum / v.count) * 10) / 10,
            completed_count: v.count,
            people_count: v.emails.size,
        }))
        .sort((a, b) => b.avg_score - a.avg_score);

    const overall_avg_score =
        completedSessions.length > 0
            ? Math.round(
                  (completedSessions.reduce((s, x) => s + x.total_score!, 0) /
                      completedSessions.length) *
                      10
              ) / 10
            : null;

    return {
        score_buckets,
        completion_trend,
        persona_effectiveness,
        department_leaderboard,
        total_completed: completedSessions.length,
        overall_avg_score,
    };
}

// Top KPI cards for the Reports → Employees tab (people enrolled / avg score /
// completion rate). GET /training/reports/employees-summary. Aggregates across
// all campaigns owned by the caller's company.
export interface ReportsEmployeesSummary {
    people_enrolled: number;
    total_employees: number;
    total_completed: number;
    completion_rate: number;
    avg_score: number | null;
}

export async function getReportsEmployeesSummary(): Promise<ReportsEmployeesSummary | null> {
    if (USE_MOCK) {
        const all = await loadCampaignsAny();
        const realSessions: TrainingSession[] = [];
        for (const c of Object.values(all)) {
            for (const s of c.sessions) {
                if (!s.is_test) realSessions.push(s);
            }
        }
        // people_enrolled / total_employees: distinct employees across ANY status.
        const total_employees = new Set(realSessions.map((s) => s.employee_email)).size;
        // total_completed: distinct employees with >=1 completed session.
        const total_completed = new Set(
            realSessions
                .filter((s) => s.status === "completed")
                .map((s) => s.employee_email),
        ).size;
        // avg_score: per-session mean over completed sessions that have a score.
        const scored = realSessions
            .filter((s) => s.status === "completed" && s.total_score !== null)
            .map((s) => s.total_score as number);
        const avg_score =
            scored.length > 0
                ? Math.round((scored.reduce((a, b) => a + b, 0) / scored.length) * 10) / 10
                : null;
        // completion_rate: % of enrolled employees who completed (avoid /0).
        const completion_rate =
            total_employees > 0
                ? Math.round((total_completed / total_employees) * 100)
                : 0;
        return {
            people_enrolled: total_employees,
            total_employees,
            total_completed,
            completion_rate,
            avg_score,
        };
    }
    try {
        const r = await axios.get(`${apiURL}/training/reports/employees-summary`, {
            headers: authHeaders(),
            timeout: 10000,
        });
        return unwrap<ReportsEmployeesSummary>(r.data) ?? null;
    } catch {
        return null;
    }
}

// Header cards for the Reports → Team Insights tab: total completed sessions /
// overall avg score / sessions in last 7 days. GET /training/reports/summary.
// All metrics are session-level and only count completed (non-test) sessions.
export interface ReportsSummary {
    total_completed_sessions: number;
    overall_avg_score: number | null;
    sessions_in_last_7days: number;
}

export async function getReportsSummary(): Promise<ReportsSummary | null> {
    if (USE_MOCK) {
        const all = await loadCampaignsAny();
        const completed: TrainingSession[] = [];
        for (const c of Object.values(all)) {
            for (const s of c.sessions) {
                if (!s.is_test && s.status === "completed") completed.push(s);
            }
        }
        // overall_avg_score: per-session mean over completed sessions that have
        // a score (completed-without-score still counts toward the counts).
        const scored = completed
            .map((s) => s.total_score)
            .filter((n): n is number => typeof n === "number");
        const overall_avg_score =
            scored.length > 0
                ? Math.round((scored.reduce((a, b) => a + b, 0) / scored.length) * 10) / 10
                : null;
        // sessions_in_last_7days: completed sessions whose completed_at is within
        // the last 7 days.
        const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
        const sessions_in_last_7days = completed.filter(
            (s) => s.completed_at && new Date(s.completed_at).getTime() >= weekAgo,
        ).length;
        return {
            total_completed_sessions: completed.length,
            overall_avg_score,
            sessions_in_last_7days,
        };
    }
    try {
        const r = await axios.get(`${apiURL}/training/reports/summary`, {
            headers: authHeaders(),
            timeout: 10000,
        });
        return unwrap<ReportsSummary>(r.data) ?? null;
    } catch {
        return null;
    }
}

// Completion trend for the Reports → Team Insights "Completion trend" chart.
// GET /training/reports/completion-trend?days=30. Returns one gap-filled point
// per UTC day (completed sessions that day), oldest → newest.
export interface CompletionTrendPoint {
    date: string; // YYYY-MM-DD
    completed: number;
}

export interface ReportsCompletionTrend {
    range_days: number;
    total_completed: number;
    peak_per_day: number;
    points: CompletionTrendPoint[];
}

export async function getReportsCompletionTrend(
    days = 30,
): Promise<ReportsCompletionTrend | null> {
    const clamped = Math.min(90, Math.max(1, Math.round(days)));
    if (USE_MOCK) {
        const all = await loadCampaignsAny();
        const completed: TrainingSession[] = [];
        for (const c of Object.values(all)) {
            for (const s of c.sessions) {
                if (!s.is_test && s.status === "completed") completed.push(s);
            }
        }
        const dayMs = 24 * 60 * 60 * 1000;
        const now = new Date();
        const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
        const points: CompletionTrendPoint[] = [];
        for (let i = clamped - 1; i >= 0; i--) {
            const dayStart = todayUtc - i * dayMs;
            const dayEnd = dayStart + dayMs;
            const count = completed.filter((s) => {
                if (!s.completed_at) return false;
                const t = new Date(s.completed_at).getTime();
                return t >= dayStart && t < dayEnd;
            }).length;
            points.push({
                date: new Date(dayStart).toISOString().slice(0, 10),
                completed: count,
            });
        }
        return {
            range_days: clamped,
            total_completed: points.reduce((sum, p) => sum + p.completed, 0),
            peak_per_day: points.reduce((mx, p) => Math.max(mx, p.completed), 0),
            points,
        };
    }
    try {
        const r = await axios.get(
            `${apiURL}/training/reports/completion-trend?days=${clamped}`,
            { headers: authHeaders(), timeout: 10000 },
        );
        return unwrap<ReportsCompletionTrend>(r.data) ?? null;
    } catch {
        return null;
    }
}

// Paginated, filterable, searchable employee roster for the Reports → Employees
// tab. GET /training/reports/employees. One row per distinct employee_email.
export interface ReportsEmployeeRow {
    employee_email: string;
    employee_name: string;
    employee_role: string | null;
    employee_department: string | null;
    employee_team_leader: string | null;
    // Raw latest session status (completed / started / in_progress / pending /
    // invited / ...). Typed loosely because the BE may return "pending", which
    // isn't part of SessionStatus.
    status: string;
    attempts: number;
    last_attempt: string | null;
    latest_score: number | null;
    avg_score: number | null;
}

export type ReportsEmployeeStatusFilter = "all" | "completed" | "in_progress" | "pending";

export type ReportsEmployeeSortBy =
    | "name"
    | "status"
    | "attempts"
    | "last_attempt"
    | "latest_score"
    | "avg_score";

export type ReportsEmployeeSortDir = "asc" | "desc";

export interface ReportsEmployeesResult {
    rows: ReportsEmployeeRow[];
    pagination: { page: number; limit: number; total: number; total_pages: number };
    status_counts: { all: number; completed: number; in_progress: number; pending: number };
}

export interface ReportsEmployeesParams {
    page?: number;
    limit?: number;
    status?: ReportsEmployeeStatusFilter;
    search?: string;
    team_leader?: string;
    sort_by?: ReportsEmployeeSortBy;
    sort?: ReportsEmployeeSortDir;
}

// Numbers/dates: nulls always sort last (both asc and desc) so empty scores
// don't jump to the top on ASC.
function cmpNullableNumber(x: number | null, y: number | null, dir: number): number {
    if (x === null && y === null) return 0;
    if (x === null) return 1;
    if (y === null) return -1;
    return (x - y) * dir;
}

// Map a raw session status into the three filter buckets the UI exposes.
function employeeStatusBucket(
    status: string,
): Exclude<ReportsEmployeeStatusFilter, "all"> {
    if (status === "completed") return "completed";
    // "scoring" (conversation done, grading running) buckets as in-progress —
    // there's no final score yet, so it isn't "completed".
    if (
        status === "started" ||
        status === "in_progress" ||
        status === "scoring"
    )
        return "in_progress";
    return "pending";
}

export async function getReportsEmployees(
    params: ReportsEmployeesParams = {},
): Promise<ReportsEmployeesResult> {
    const page = Math.max(1, params.page ?? 1);
    const limit = Math.min(100, Math.max(1, params.limit ?? 10));
    const statusFilter = params.status ?? "all";
    const search = (params.search ?? "").trim();
    const sortBy: ReportsEmployeeSortBy = params.sort_by ?? "last_attempt";
    const sortDir: ReportsEmployeeSortDir = params.sort ?? "desc";

    if (USE_MOCK) {
        const people = await listAllPeople();
        const allRows: ReportsEmployeeRow[] = people.map((p) => {
            const scored = p.attempts
                .map((a) => a.total_score)
                .filter((n): n is number => typeof n === "number");
            return {
                employee_email: p.email,
                employee_name: p.name,
                employee_role: p.role,
                employee_department: p.department,
                employee_team_leader: null,
                status: p.latest_session.status,
                attempts: p.attempts.length,
                last_attempt:
                    p.latest_session.completed_at ?? p.latest_session.started_at ?? null,
                latest_score: p.latest_session.total_score,
                avg_score:
                    scored.length > 0
                        ? Math.round((scored.reduce((a, b) => a + b, 0) / scored.length) * 10) / 10
                        : null,
            };
        });
        const q = search.toLowerCase();
        const searched = q
            ? allRows.filter((r) =>
                  `${r.employee_name} ${r.employee_email} ${r.employee_role ?? ""} ${r.employee_department ?? ""}`
                      .toLowerCase()
                      .includes(q),
              )
            : allRows;
        const status_counts = {
            all: searched.length,
            completed: searched.filter((r) => employeeStatusBucket(r.status) === "completed").length,
            in_progress: searched.filter((r) => employeeStatusBucket(r.status) === "in_progress").length,
            pending: searched.filter((r) => employeeStatusBucket(r.status) === "pending").length,
        };
        const filtered =
            statusFilter === "all"
                ? searched
                : searched.filter((r) => employeeStatusBucket(r.status) === statusFilter);

        const dir = sortDir === "asc" ? 1 : -1;
        const sorted = [...filtered].sort((a, b) => {
            switch (sortBy) {
                case "name":
                    return a.employee_name.localeCompare(b.employee_name, undefined, { sensitivity: "base" }) * dir;
                case "status":
                    return String(a.status).localeCompare(String(b.status), undefined, { sensitivity: "base" }) * dir;
                case "attempts":
                    return (a.attempts - b.attempts) * dir;
                case "latest_score":
                    return cmpNullableNumber(a.latest_score, b.latest_score, dir);
                case "avg_score":
                    return cmpNullableNumber(a.avg_score, b.avg_score, dir);
                case "last_attempt":
                    return cmpNullableNumber(
                        a.last_attempt ? new Date(a.last_attempt).getTime() : null,
                        b.last_attempt ? new Date(b.last_attempt).getTime() : null,
                        dir,
                    );
                default:
                    return 0;
            }
        });

        const total = sorted.length;
        const total_pages = Math.max(1, Math.ceil(total / limit));
        const start = (page - 1) * limit;
        return {
            rows: sorted.slice(start, start + limit),
            pagination: { page, limit, total, total_pages },
            status_counts,
        };
    }

    try {
        const qs = new URLSearchParams();
        qs.set("page", String(page));
        qs.set("limit", String(limit));
        if (statusFilter !== "all") qs.set("status", statusFilter);
        if (search) qs.set("search", search);
        qs.set("sort_by", sortBy);
        qs.set("sort", sortDir);
        const r = await axios.get(
            `${apiURL}/training/reports/employees?${qs.toString()}`,
            { headers: authHeaders(), timeout: 10000 },
        );
        const rows = unwrap<ReportsEmployeeRow[]>(r.data) ?? [];
        const meta = (r.data as {
            meta?: {
                pagination?: ReportsEmployeesResult["pagination"];
                status_counts?: ReportsEmployeesResult["status_counts"];
            };
        }).meta;
        return {
            rows,
            pagination: meta?.pagination ?? { page, limit, total: rows.length, total_pages: 1 },
            status_counts:
                meta?.status_counts ?? { all: rows.length, completed: 0, in_progress: 0, pending: 0 },
        };
    } catch {
        return {
            rows: [],
            pagination: { page, limit, total: 0, total_pages: 0 },
            status_counts: { all: 0, completed: 0, in_progress: 0, pending: 0 },
        };
    }
}

// One employee's attempt history (Employee Profile drawer) —
// GET /training/reports/employees/attempts?email=<email>. Mapped to the
// TrainingSession[] shape the drawer already consumes.
export async function getEmployeeAttempts(email: string, campaignId?: string): Promise<TrainingSession[]> {
    if (USE_MOCK) {
        const people = await listAllPeople();
        const person = people.find((p) => p.email === email);
        return person ? person.attempts : [];
    }
    try {
        const attemptsParams = new URLSearchParams({ email });
        if (campaignId) attemptsParams.set("campaign_id", campaignId);
        const r = await axios.get(
            `${apiURL}/training/reports/employees/attempts?${attemptsParams.toString()}`,
            { headers: authHeaders(), timeout: 10000 },
        );
        const body = unwrap<{
            employee_email?: string;
            employee_name?: string;
            employee_role?: string | null;
            employee_department?: string | null;
            attempts?: Array<Record<string, unknown>>;
        }>(r.data) ?? {};
        const rawAttempts = Array.isArray(body.attempts) ? body.attempts : [];
        return rawAttempts.map((a) => ({
            id: (a.token ?? a.id ?? "") as string,
            campaign_id: (a.campaign_id ?? campaignId ?? "") as string,
            employee_email: (body.employee_email ?? email) as string,
            employee_name: (body.employee_name ?? "") as string,
            employee_role: (body.employee_role ?? null) as string | null,
            employee_department: (body.employee_department ?? null) as string | null,
            token: (a.token ?? "") as string,
            attempt_number: (a.attempt_number ?? 0) as number,
            status: (a.status ?? "invited") as SessionStatus,
            started_at: (a.started_at ?? null) as string | null,
            completed_at: (a.completed_at ?? null) as string | null,
            rubric_scores: (a.rubric_scores ?? null) as RubricScores | null,
            total_score: (a.total_score ?? null) as number | null,
            qualitative_feedback: (a.qualitative_feedback ?? null) as string | null,
        }));
    } catch {
        return [];
    }
}

// One employee's activity feed (Employee Profile → Activity Log tab) —
// GET /training/reports/employees/activity-log?email=<email>.
export async function getEmployeeActivityLog(email: string, campaignId?: string): Promise<ActivityEvent[]> {
    if (USE_MOCK) {
        const all = await loadCampaignsAny();
        const events: ActivityEvent[] = [];
        for (const c of Object.values(all)) {
            for (const e of deriveEmployeeTimeline(c, email)) {
                events.push(e);
            }
        }
        return events.sort((a, b) => b.ts.localeCompare(a.ts));
    }
    try {
        const activityParams = new URLSearchParams({ email });
        if (campaignId) activityParams.set("campaign_id", campaignId);
        const r = await axios.get(
            `${apiURL}/training/reports/employees/activity-log?${activityParams.toString()}`,
            { headers: authHeaders(), timeout: 10000 },
        );
        return unwrap<ActivityEvent[]>(r.data) ?? [];
    } catch {
        return [];
    }
}

export function deriveActivityLog(detail: TrainingCampaignDetail, hrName?: string): ActivityEvent[] {
    const events: ActivityEvent[] = [];
    const realSessions = detail.sessions.filter((s) => !s.is_test);
    // invite_sent pushed first so it sorts above campaign_created when timestamps are equal
    if (realSessions.length > 0) {
        events.push({
            id: `invites-batch-${detail.id}`,
            ts: detail.created_at,
            type: "invite_sent",
            detail: `Invites sent to ${realSessions.length} employee${realSessions.length === 1 ? "" : "s"}`,
            performed_by: hrName,
        });
    }
    events.push({
        id: `created-${detail.id}`,
        ts: detail.created_at,
        type: "campaign_created",
        detail: detail.name,
        performed_by: hrName,
    });
    return events.sort((a, b) => b.ts.localeCompare(a.ts));
}

export function deriveEmployeeTimeline(
    detail: TrainingCampaignDetail,
    email: string,
    hrName?: string,
): ActivityEvent[] {
    const events: ActivityEvent[] = [];
    const sessions = detail.sessions.filter((s) => !s.is_test && s.employee_email === email);
    if (sessions.length > 0) {
        events.push({
            id: `invite-emp-${email}-${detail.id}`,
            ts: detail.created_at,
            type: "invite_sent",
            employee_name: sessions[0].employee_name,
            performed_by: hrName,
        });
    }
    for (const s of sessions) {
        if (s.started_at) {
            events.push({
                id: `start-${s.id}`,
                ts: s.started_at,
                type: s.attempt_number > 1 ? "retry_started" : "session_started",
                employee_name: s.employee_name,
                detail: s.attempt_number > 1 ? `Attempt ${s.attempt_number}` : undefined,
                performed_by: s.employee_name,
            });
        }
        if (s.completed_at && s.status === "completed") {
            events.push({
                id: `complete-${s.id}`,
                ts: s.completed_at,
                type: "session_completed",
                employee_name: s.employee_name,
                detail: s.total_score !== null ? `Scored ${s.total_score}/100` : undefined,
                performed_by: s.employee_name,
            });
        }
        if (s.status === "abandoned") {
            events.push({
                id: `abandon-${s.id}`,
                ts: s.started_at ?? detail.created_at,
                type: "session_abandoned",
                employee_name: s.employee_name,
                performed_by: s.employee_name,
            });
        }
    }
    return events.sort((a, b) => b.ts.localeCompare(a.ts));
}

export interface CampaignInsights {
    headline: string;
    bullets: string[];
    weakest_dimension: keyof RubricScores | null;
    weakest_dimension_avg: number | null;
    top_performer: { name: string; score: number } | null;
    most_improved: { name: string; from: number; to: number } | null;
    completion_velocity: string | null;
}

export async function getCampaignInsights(campaignId: string): Promise<CampaignInsights | null> {
    const detail = (await loadCampaignsAny())[campaignId];
    if (!detail) return null;
    return computeCampaignInsights(detail);
}

export function computeCampaignInsights(detail: TrainingCampaignDetail): CampaignInsights {
    const realSessions = detail.sessions.filter((s) => !s.is_test);
    const completed = realSessions.filter(
        (s) => s.status === "completed" && s.rubric_scores && s.total_score !== null
    );
    const total = realSessions.length;
    const completionPct = total > 0 ? Math.round((completed.length / total) * 100) : 0;

    if (completed.length === 0) {
        return {
            headline:
                total === 0
                    ? "No employees enrolled yet."
                    : `${total} employee${total === 1 ? "" : "s"} invited. Waiting on first completion.`,
            bullets: [
                completionPct === 0
                    ? "Send a follow-up nudge to drive first attempts."
                    : `Currently ${completionPct}% complete.`,
            ],
            weakest_dimension: null,
            weakest_dimension_avg: null,
            top_performer: null,
            most_improved: null,
            completion_velocity: null,
        };
    }

    // Weakest rubric dimension across the campaign
    const dims = Object.keys(RUBRIC_LABELS) as (keyof RubricScores)[];
    const dimAverages = dims.map((d) => {
        const sum = completed.reduce((acc, s) => acc + (s.rubric_scores![d] || 0), 0);
        return { dim: d, avg: sum / completed.length };
    });
    dimAverages.sort((a, b) => a.avg - b.avg);
    const weakest = dimAverages[0];
    const strongest = dimAverages[dimAverages.length - 1];

    // Top performer
    const sortedByScore = [...completed].sort(
        (a, b) => (b.total_score || 0) - (a.total_score || 0)
    );
    const top = sortedByScore[0];

    // Most improved (employee with multiple attempts)
    const byEmployee = new Map<string, TrainingSession[]>();
    for (const s of realSessions) {
        if (!byEmployee.has(s.employee_email)) byEmployee.set(s.employee_email, []);
        byEmployee.get(s.employee_email)!.push(s);
    }
    let mostImproved: { name: string; from: number; to: number } | null = null;
    for (const list of Array.from(byEmployee.values())) {
        const ordered = list
            .filter((s: TrainingSession) => s.total_score !== null)
            .sort((a: TrainingSession, b: TrainingSession) => a.attempt_number - b.attempt_number);
        if (ordered.length < 2) continue;
        const from = ordered[0].total_score!;
        const to = ordered[ordered.length - 1].total_score!;
        const delta = to - from;
        if (!mostImproved || delta > mostImproved.to - mostImproved.from) {
            if (delta > 0) {
                mostImproved = { name: ordered[0].employee_name, from, to };
            }
        }
    }

    // Completion velocity
    const completedAt = completed
        .map((s) => (s.completed_at ? new Date(s.completed_at).getTime() : null))
        .filter((n): n is number => n !== null);
    const created = new Date(detail.created_at).getTime();
    const lastCompletion = completedAt.length > 0 ? Math.max(...completedAt) : null;
    const days =
        lastCompletion && created
            ? Math.max(1, Math.round((lastCompletion - created) / (24 * 60 * 60 * 1000)))
            : null;
    const velocity =
        days !== null
            ? `${completed.length} of ${total} completed over ${days} day${days === 1 ? "" : "s"}`
            : null;

    const avgTotal =
        completed.reduce((acc, s) => acc + (s.total_score || 0), 0) / completed.length;
    const headline =
        avgTotal >= 76
            ? `Strong run: avg ${avgTotal.toFixed(1)}/100 across ${completed.length} attempts.`
            : avgTotal >= 56
            ? `Mixed results: avg ${avgTotal.toFixed(1)}/100, with ${RUBRIC_LABELS[weakest.dim].toLowerCase()} the team's sticking point.`
            : `Team is struggling: avg ${avgTotal.toFixed(1)}/100. Focus coaching on ${RUBRIC_LABELS[weakest.dim].toLowerCase()}.`;

    const bullets: string[] = [];
    bullets.push(
        `Weakest dimension: ${RUBRIC_LABELS[weakest.dim]} (avg ${weakest.avg.toFixed(1)}/5). Strongest: ${RUBRIC_LABELS[strongest.dim]} (avg ${strongest.avg.toFixed(1)}/5).`
    );
    if (top) {
        bullets.push(`Top performer: ${top.employee_name} at ${top.total_score}/100.`);
    }
    if (mostImproved) {
        bullets.push(
            `Most improved: ${mostImproved.name} (${mostImproved.from}/100 → ${mostImproved.to}/100 across attempts).`
        );
    }
    if (velocity) {
        bullets.push(velocity);
    }

    return {
        headline,
        bullets,
        weakest_dimension: weakest.dim,
        weakest_dimension_avg: Math.round(weakest.avg * 10) / 10,
        top_performer: top
            ? { name: top.employee_name, score: top.total_score! }
            : null,
        most_improved: mostImproved,
        completion_velocity: velocity,
    };
}

// ── Team insights · Elements ranking ────────────────────────────────────────
// Per-element team aggregate that backs the "Elements" view of the Team
// insights card. Ranks the 5 fixed rubric elements by the team's mean holistic
// 1–5 category score — for one campaign, or (the default) the cross-campaign
// "All active campaigns" aggregate.
//
// Backend contract — GET /training/insights/elements (BE #3191):
//   ?campaign_id=...  → that one campaign (per-campaign ranking, unchanged).
//   (omitted)         → cross-campaign aggregate over ACTIVE campaigns with
//                       ≥1 scored session (draft + archived excluded), means
//                       session-weighted across campaigns. Adds `campaigns_count`.
// Response (unwrapped from StandardResponse.data):
//   { elements: [{ element, mean, sessions }], excluded_sessions, campaigns_count? }
// - `element`  = one of the 5 fixed rubric keys (keyof RubricScores); all 5 are
//                always returned.
// - `mean`     = arithmetic mean of the holistic 1–5 score, 1 decimal,
//                NON-null (0 when no session contributed). FE does NOT recompute.
// - `sessions` = count of completed sessions contributing to that element.
// - `excluded_sessions` = completed sessions dropped for having no snapshotted
//                rubric — surfaced in the UI, never silently swallowed.
// - `campaigns_count` = number of active, scored campaigns in the aggregate
//                (all-campaigns mode only) — backs the "All active campaigns (X)"
//                label. Absent for the per-campaign request.
export interface ElementInsight {
    element: keyof RubricScores;
    mean: number;
    sessions: number;
}

export interface ElementInsights {
    elements: ElementInsight[];
    excluded_sessions: number;
    // Only populated in the all-campaigns aggregate (campaignId omitted).
    campaigns_count?: number;
}

// `campaignId === null` requests the cross-campaign aggregate (no campaign_id
// param); a string requests that one campaign's ranking.
export async function getElementInsights(
    campaignId: string | null,
): Promise<ElementInsights | null> {
    if (USE_MOCK) {
        const all = await loadCampaignsAny();
        const keys = Object.keys(RUBRIC_LABELS) as (keyof RubricScores)[];
        // All-campaigns aggregate: active campaigns with ≥1 scored session
        // (draft + archived excluded). Per-campaign: just that campaign.
        const details =
            campaignId === null
                ? Object.values(all).filter((c) => c.status === "active")
                : all[campaignId]
                ? [all[campaignId]]
                : null;
        if (!details) return null;
        const sums: Record<string, number> = {};
        const counts: Record<string, number> = {};
        keys.forEach((k) => {
            sums[k] = 0;
            counts[k] = 0;
        });
        let excluded = 0;
        const scoredCampaignIds = new Set<string>();
        for (const detail of details) {
            const completed = detail.sessions.filter(
                (s) => !s.is_test && s.status === "completed",
            );
            for (const s of completed) {
                const rs = s.rubric_scores;
                let contributed = false;
                if (rs) {
                    for (const k of keys) {
                        const v = rs[k];
                        if (typeof v === "number" && !Number.isNaN(v)) {
                            sums[k] += v;
                            counts[k] += 1;
                            contributed = true;
                        }
                    }
                }
                if (contributed) scoredCampaignIds.add(detail.id);
                else excluded += 1;
            }
        }
        return {
            elements: keys.map((k) => ({
                element: k,
                // Session-weighted across campaigns: sums/counts pool every
                // contributing session, so a busy campaign weighs more.
                mean: counts[k] ? Math.round((sums[k] / counts[k]) * 10) / 10 : 0,
                sessions: counts[k],
            })),
            excluded_sessions: excluded,
            ...(campaignId === null
                ? { campaigns_count: scoredCampaignIds.size }
                : {}),
        };
    }
    try {
        // Omit campaign_id entirely for the all-campaigns aggregate.
        const url =
            campaignId === null
                ? `${apiURL}/training/insights/elements`
                : `${apiURL}/training/insights/elements?campaign_id=${encodeURIComponent(campaignId)}`;
        const r = await axios.get(url, { headers: authHeaders(), timeout: 10000 });
        const data = unwrap<{
            elements?: Array<{ element: string; mean: number; sessions: number }>;
            excluded_sessions?: number;
            campaigns_count?: number;
        }>(r.data);
        if (!data || !Array.isArray(data.elements)) return null;
        return {
            elements: data.elements.map((e) => ({
                element: e.element as keyof RubricScores,
                mean: e.mean ?? 0,
                sessions: e.sessions ?? 0,
            })),
            excluded_sessions: data.excluded_sessions ?? 0,
            ...(campaignId === null
                ? { campaigns_count: data.campaigns_count ?? 0 }
                : {}),
        };
    } catch (err) {
        catchErrorLog(
            `[training] getElementInsights failed for ${campaignId === null ? "all campaigns" : `campaign ${campaignId}`}: ${err instanceof Error ? err.message : String(err)}`,
        );
        return null;
    }
}

export function resetDemoData() {
    localStorage.removeItem(STORAGE_KEY);
}

export function seedDemoCampaigns() {
    const all = loadCampaigns();
    if (Object.keys(all).length > 0) return;

    const seedEmployees: Array<[string, string, string, string]> = [
        ["Aisha Rahman", "aisha.rahman@pos.demo", "Counter Officer", "Branch Operations"],
        ["Bala Kumar", "bala.kumar@pos.demo", "Counter Officer", "Branch Operations"],
        ["Chong Wei Ming", "chong.wm@pos.demo", "Senior Counter Officer", "Branch Operations"],
        ["Devi Nair", "devi.nair@pos.demo", "Branch Supervisor", "Branch Operations"],
        ["Ezra Hassan", "ezra.hassan@pos.demo", "Counter Officer", "Branch Operations"],
        ["Farah Iskandar", "farah.iskandar@pos.demo", "Customer Service Rep", "Contact Centre"],
        ["Gavin Ong", "gavin.ong@pos.demo", "Customer Service Rep", "Contact Centre"],
        ["Hana Putri", "hana.putri@pos.demo", "Counter Officer", "Branch Operations"],
        ["Imran Kassim", "imran.kassim@pos.demo", "Branch Supervisor", "Branch Operations"],
        ["Jia Hui Lim", "jiahui.lim@pos.demo", "Customer Service Rep", "Contact Centre"],
        ["Kavitha Singh", "kavitha.singh@pos.demo", "Senior CSR", "Contact Centre"],
        ["Lina Chen", "lina.chen@pos.demo", "Claims Officer", "Claims & Disputes"],
    ];

    const now = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;

    type Seed = {
        name: string;
        type: TrainingType;
        persona: string;
        createdDaysAgo: number;
        employees: number;
        completedRatio: number;
        scoreBias: number;
    };
    const seeds: Seed[] = [
        {
            name: "Pos Laju Q2 — Late Parcel Handling",
            type: "customer_service",
            persona: "cs_frustrated_customer",
            createdDaysAgo: 5,
            employees: 8,
            completedRatio: 0.75,
            scoreBias: 18,
        },
        {
            name: "Branch Counter — Elderly Customer Care",
            type: "customer_service",
            persona: "cs_confused_elder",
            createdDaysAgo: 11,
            employees: 6,
            completedRatio: 0.5,
            scoreBias: 16,
        },
        {
            name: "Claims Team — Lost Parcel Escalation",
            type: "customer_service",
            persona: "cs_refund_demander",
            createdDaysAgo: 2,
            employees: 5,
            completedRatio: 0.2,
            scoreBias: 14,
        },
    ];

    const allCampaigns: Record<string, TrainingCampaignDetail> = {};
    seeds.forEach((seed) => {
        const id = uid("camp");
        const created = new Date(now - seed.createdDaysAgo * dayMs).toISOString();
        const employees = seedEmployees.slice(0, seed.employees);
        const sessions: TrainingSession[] = [];
        employees.forEach(([name, email, role, department], i) => {
            const employeeCompleted = i / employees.length < seed.completedRatio;
            const employeeInProgress = !employeeCompleted && i === Math.floor(seed.employees * seed.completedRatio);
            // Some completed employees get 2-3 attempts to make sparkline meaningful
            const attemptsForThisEmployee = employeeCompleted && i % 2 === 0 ? 3 : employeeCompleted && i % 3 === 0 ? 2 : 1;
            const sharedToken = uid("tok");

            for (let attempt = 1; attempt <= attemptsForThisEmployee; attempt++) {
                const isLatest = attempt === attemptsForThisEmployee;
                const status: TrainingSession["status"] = employeeCompleted
                    ? "completed"
                    : employeeInProgress && isLatest
                    ? "in_progress"
                    : "invited";
                const dayOffset = (seed.createdDaysAgo - 1) - (attemptsForThisEmployee - attempt) * 2;
                const startedAt = employeeCompleted
                    ? new Date(now - Math.max(0, dayOffset) * dayMs - i * 3600_000).toISOString()
                    : employeeInProgress && isLatest
                    ? new Date(now - 600_000).toISOString()
                    : null;
                const completedAt = employeeCompleted
                    ? new Date(now - Math.max(0, dayOffset) * dayMs - i * 3600_000 + 6 * 60_000).toISOString()
                    : null;

                let scores: RubricScores | null = null;
                let total: number | null = null;
                let summary: string | null = null;
                let strengths: string | null = null;
                let growth: string | null = null;
                let highlight: TrainingSession["transcript_highlight"] = null;
                let transcript: TranscriptTurn[] | null = null;
                if (employeeCompleted) {
                    // Slight upward trajectory across attempts, with noise
                    const trend = (attempt - 1) * 0.4;
                    const variance = () =>
                        Math.max(
                            1,
                            Math.min(
                                5,
                                Math.round((seed.scoreBias / 5) + trend + (Math.random() * 1.6 - 0.8))
                            )
                        );
                    scores = {
                        empathy: variance(),
                        problem_resolution: variance(),
                        product_knowledge: variance(),
                        tone: variance(),
                        call_handling: variance(),
                    };
                    total =
                        scores.empathy +
                        scores.problem_resolution +
                        scores.product_knowledge +
                        scores.tone +
                        scores.call_handling;
                    const fb = buildFeedback(scores, total);
                    summary = fb.summary;
                    strengths = fb.strengths;
                    growth = fb.growth;
                    highlight = fb.highlight;
                    transcript = transcriptForPersona(seed.persona);
                }

                sessions.push({
                    id: uid("sess"),
                    campaign_id: id,
                    employee_email: email,
                    employee_name: name,
                    employee_role: role,
                    employee_department: department,
                    token: isLatest ? sharedToken : uid("tok"),
                    attempt_number: attempt,
                    status,
                    started_at: startedAt,
                    completed_at: completedAt,
                    rubric_scores: scores,
                    total_score: total,
                    qualitative_feedback: summary,
                    feedback_strengths: strengths,
                    feedback_growth: growth,
                    transcript_highlight: highlight,
                    transcript,
                });
            }
        });

        const completedCount = sessions.filter((s) => s.status === "completed").length;
        const scored = sessions
            .map((s) => s.total_score)
            .filter((n): n is number => typeof n === "number");
        const avg =
            scored.length > 0
                ? Math.round((scored.reduce((a, b) => a + b, 0) / scored.length) * 10) / 10
                : null;

        allCampaigns[id] = {
            id,
            name: seed.name,
            training_type: seed.type,
            persona_id: seed.persona,
            status: "active",
            created_at: created,
            employee_count: sessions.length,
            completed_count: completedCount,
            avg_score: avg,
            sessions,
        };
    });

    saveCampaigns(allCampaigns);
}



// ---- API service layer (Iteration 2 swap-in target) -----------------
// When VITE_USE_MOCK_TRAINING is "false", these will hit the real backend.
// Today they delegate to the mock so behaviour is unchanged. Backend
// engineer just needs to fill in the fetch() bodies once endpoints land.

const USE_MOCK = import.meta.env.VITE_USE_MOCK_TRAINING === "true";

export async function apiGetSession(token: string) {
    return getSessionByToken(token);
}

export interface ApiStartSessionResponse {
    assessment_id: string;
    persona_id: string;
    voice_key: string;
}

export async function apiStartSession(token: string): Promise<ApiStartSessionResponse | null> {
    if (USE_MOCK) {
        await sleep(200);
        const r = await getSessionByToken(token);
        if (!r) return null;
        return {
            assessment_id: r.persona.assessment_id ?? `mock-${r.persona.id}`,
            persona_id: r.persona.id,
            voice_key: r.persona.voice_key ?? r.persona.id,
        };
    }
    try {
        const r = await axios.post(
            `${apiURL}/training/session/${token}/start`,
            {},
            { headers: publicHeaders(), timeout: 30000 }
        );
        return unwrap<ApiStartSessionResponse>(r.data) ?? null;
    } catch {
        return null;
    }
}

// Pre-flight gate for the "Begin Roleplay" click (BE handoff 2026-06-03).
// Called BEFORE POST /start so a trainee can't re-enter — and thereby
// overwrite — a session that's already been used (finished + scored, or still
// being scored). The BE encodes the verdict in the HTTP status:
//   200 → can start (fresh / resumable / test session)
//   409 → already used; data.used_state is "completed" | "scoring"
//   404 → token not found
// Anything else (network blip, 5xx) is a soft failure: we let the user through
// and rely on POST /start, which stays the authoritative gate.
export type CanStartOutcome =
    | { kind: "ok" }
    | { kind: "used"; usedState: "completed" | "scoring" }
    | { kind: "not_found" }
    | { kind: "error" };

export async function apiCanStartSession(token: string): Promise<CanStartOutcome> {
    if (USE_MOCK) {
        await sleep(150);
        return { kind: "ok" };
    }
    try {
        await axios.get(`${apiURL}/training/session/${token}/can-start`, {
            headers: publicHeaders(),
            timeout: 10000,
        });
        return { kind: "ok" };
    } catch (err) {
        if (axios.isAxiosError(err)) {
            const status = err.response?.status;
            if (status === 409) {
                const body = err.response?.data as
                    | { data?: { used_state?: string } }
                    | undefined;
                const usedState =
                    body?.data?.used_state === "scoring" ? "scoring" : "completed";
                return { kind: "used", usedState };
            }
            if (status === 404) {
                return { kind: "not_found" };
            }
        }
        return { kind: "error" };
    }
}

// --- Voice-call lifecycle (ElevenLabs handoff) ---
//
// Three endpoints called from InterviewVoiceOnlyActive once the candidate
// hits Start. The BE side wires them as documented in the new-endpoints
// contract: signed-url issuance → conversation_id storage → completion
// (which triggers async scoring).

export interface VoiceSignedUrlResponse {
    signed_url: string;
}

export async function apiGetVoiceSignedUrl(token: string): Promise<VoiceSignedUrlResponse | null> {
    if (USE_MOCK) {
        await sleep(100);
        return { signed_url: "wss://mock.elevenlabs.invalid/conversation" };
    }
    try {
        const r = await axios.get(
            `${apiURL}/training/session/${token}/voice-signed-url`,
            { headers: publicHeaders(), timeout: 10000 }
        );
        return unwrap<VoiceSignedUrlResponse>(r.data) ?? null;
    } catch {
        return null;
    }
}

export async function apiVoiceConnect(
    token: string,
    elevenlabsConversationId: string
): Promise<boolean> {
    if (USE_MOCK) {
        await sleep(50);
        return true;
    }
    try {
        await axios.post(
            `${apiURL}/training/session/${token}/voice-connect`,
            { elevenlabs_conversation_id: elevenlabsConversationId },
            { headers: publicHeaders(), timeout: 10000 }
        );
        return true;
    } catch {
        return false;
    }
}

export async function apiCompleteSession(token: string): Promise<boolean> {
    if (USE_MOCK) {
        await sleep(50);
        return true;
    }
    try {
        await axios.post(
            `${apiURL}/training/session/${token}/complete`,
            {},
            { headers: publicHeaders(), timeout: 10000 }
        );
        return true;
    } catch {
        return false;
    }
}

export async function apiGetPreflightAudio(
    token: string
): Promise<{ audio_base64: string; content_type: string; greeting_text: string } | null> {
    if (USE_MOCK) {
        return null; // skip preflight in mock mode
    }
    try {
        const r = await axios.get(
            `${apiURL}/training/session/${token}/preflight-audio`,
            { headers: publicHeaders(), timeout: 20000 }
        );
        return unwrap<{ audio_base64: string; content_type: string; greeting_text: string }>(r.data) ?? null;
    } catch {
        return null;
    }
}

export async function apiPreflightStt(
    token: string,
    blob: Blob,
    mimeType: string
): Promise<{ transcription: string; answered_yes: boolean } | null> {
    if (USE_MOCK) {
        return { transcription: "yes", answered_yes: true };
    }
    try {
        const form = new FormData();
        form.append("audio", blob, `reply.${mimeType.includes("webm") ? "webm" : "wav"}`);
        const r = await axios.post(
            `${apiURL}/training/session/${token}/preflight-stt`,
            form,
            { timeout: 20000 }
        );
        return unwrap<{ transcription: string; answered_yes: boolean }>(r.data) ?? null;
    } catch {
        return null;
    }
}

export async function sendTrainingSessionFeedback(
    token: string,
    payload: {
        rating: number;
        category: string;
        feedback: string;
        interview_duration: string;
        conversation_id: string;
        metadata: Record<string, unknown>;
    }
): Promise<void> {
    try {
        await axios.post(
            `${apiURL}/training/session/${token}/feedback`,
            payload,
            { headers: { "Content-Type": "application/json" }, timeout: 10000 }
        );
    } catch (err) {
        catchErrorLog(`[training] feedback submit failed: ${err}`);
    }
}

export async function apiGetAssessmentForSession(token: string): Promise<InterviewAssessment | null> {
    const start = await apiStartSession(token);
    if (!start) return null;
    if (USE_MOCK) {
        const session = await getSessionByToken(token);
        if (!session) return null;
        return {
            id: start.assessment_id,
            type: "Interview",
            status: "Active",
            content: {
                interviewName: session.persona.name,
                description: session.persona.briefing,
                maxDuration: 720,
                interviewContext: session.persona.briefing,
                aiName: session.persona.name,
                interviewLanguage: "en-US",
                voice: "female",
                sections: [],
                category: "training",
                interviewerPersona: session.persona.briefing,
                toneAndCommunication: { enabled: false },
                interviewFlowPrinciples: { enabled: false },
                informationToShare: { enabled: false, value: "" },
            },
        };
    }
    const data = await getAssessment(start.assessment_id);
    return data as InterviewAssessment;
}

export type PollResultsOutcome =
    | { kind: "ready"; session: TrainingSession }
    | { kind: "scoring" }
    | { kind: "failed"; reason: string };

/**
 * Poll the BE for scoring results.
 *
 * Real BE contract (post-2026-05-04 handoff): always returns 200 with a
 * `status` lifecycle field. We map that to a discriminated outcome the
 * caller can use to decide whether to keep polling, render results, or
 * surface a failure card.
 *
 * Mock branch: synthesizes a "ready" outcome from the localStorage session
 * row.
 */
export async function apiPollResults(token: string): Promise<PollResultsOutcome> {
    if (USE_MOCK) {
        const r = await getSessionByToken(token);
        if (r?.session) return { kind: "ready", session: r.session };
        return { kind: "scoring" };
    }
    try {
        const r = await axios.get(`${apiURL}/training/session/${token}/results`, {
            headers: publicHeaders(),
            timeout: 10000,
        });
        const body = unwrap<{
            status: "scoring" | "ready" | "failed";
            session: TrainingSession;
            failed_reason?: string;
        }>(r.data);
        if (!body) return { kind: "scoring" };
        if (body.status === "ready") {
            return { kind: "ready", session: body.session };
        }
        if (body.status === "failed") {
            return {
                kind: "failed",
                reason: body.failed_reason || "Scoring failed",
            };
        }
        return { kind: "scoring" };
    } catch {
        // Network blip or 404. Treat as still-scoring; the loop's overall
        // budget governs when we give up.
        return { kind: "scoring" };
    }
}

export type InvitationStatus =
    | "queued"
    | "sent"
    | "opened"
    | "clicked"
    | "bounced"
    | "started"
    | "completed";

export interface InvitationRow {
    employee_email: string;
    employee_name: string;
    status: InvitationStatus;
    last_event_at: string;
    last_attempt_count: number;
    bounce_reason?: string;
    // BE-supplied: the employee's most recent scored attempt (out of 100), or
    // null when they have no completed/scored attempt yet.
    latest_score?: number | null;
    // BE-supplied: the employee's latest training/session status (invited /
    // started / in_progress / completed / ...). Authoritative source for the
    // employee's current status, preferred over the latest session row.
    last_status?: SessionStatus | null;
}

function hashSeed(s: string): number {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    return Math.abs(h);
}

function deriveInvitationStatus(s: TrainingSession): InvitationStatus {
    if (s.status === "completed") return "completed";
    if (s.status === "in_progress" || s.started_at) return "started";
    const seed = hashSeed(s.token);
    const bucket = seed % 100;
    if (bucket < 8) return "bounced";
    if (bucket < 28) return "opened";
    if (bucket < 38) return "clicked";
    if (bucket < 92) return "sent";
    return "queued";
}

export async function getCampaignInvitations(
    campaignId: string
): Promise<InvitationRow[]> {
    if (USE_MOCK) {
        await sleep(120);
        const detail = (await loadCampaignsAny())[campaignId];
        if (!detail) return [];
        const latest = new Map<string, TrainingSession>();
        for (const s of detail.sessions) {
            if (s.is_test) continue;
            const prev = latest.get(s.employee_email);
            if (!prev || s.attempt_number > prev.attempt_number) {
                latest.set(s.employee_email, s);
            }
        }
        return Array.from(latest.values()).map((s) => {
            const status = deriveInvitationStatus(s);
            const seed = hashSeed(s.token);
            const minutesAgo = (seed % 720) + 5;
            const ts = new Date(Date.now() - minutesAgo * 60_000).toISOString();
            return {
                employee_email: s.employee_email,
                employee_name: s.employee_name,
                status,
                last_event_at: s.completed_at ?? s.started_at ?? ts,
                last_attempt_count: 1,
                bounce_reason:
                    status === "bounced" ? "Mailbox full or address invalid" : undefined,
                latest_score: s.total_score ?? null,
                last_status: s.status,
            };
        });
    }
    // Let real errors propagate — InvitationsPanel renders an error card via
    // its catch handler. Swallowing here would surface as a misleading "No
    // invitations sent yet" empty state on a 500/network failure.
    const r = await axios.get(
        `${apiURL}/training/campaigns/${campaignId}/invitations`,
        { headers: authHeaders(), timeout: 10000 }
    );
    return unwrap<InvitationRow[]>(r.data) ?? [];
}

// --- Rubric ---

export async function getRubric(campaignId: string): Promise<RubricCriterion[]> {
    if (USE_MOCK) {
        await sleep(150);
        return Object.entries(RUBRIC_LABELS).map(([key, label]) => ({
            id: key,
            name: label,
            weight: 1.0,
            description: `How well the employee demonstrated ${label.toLowerCase()}.`,
            correct_answer: "",
            scoring_guide: `Score 5 if fully demonstrated, 3 if partial, 1 if absent.`,
        }));
    }
    const r = await axios.get(
        `${apiURL}/training/campaigns/${campaignId}/rubric`,
        { headers: authHeaders(), timeout: 10000 }
    );
    return unwrap<RubricCriterion[]>(r.data) ?? [];
}

export async function updateRubric(
    campaignId: string,
    criteria: RubricCriterion[]
): Promise<void> {
    if (USE_MOCK) {
        await sleep(200);
        return;
    }
    await axios.put(
        `${apiURL}/training/campaigns/${campaignId}/rubric`,
        { criteria },
        { headers: authHeaders(), timeout: 10000 }
    );
}

// --- Session Results & History ---

export interface SessionResultsResponse {
    session: TrainingSession;
    rubric_criteria: RubricCriterion[];
}

function secondsToClock(total: number): string {
    const s = Math.max(0, Math.floor(total));
    return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
}

// Derive a renderable `ts` from the BE results transcript's `timestamp` field.
// The transcript adapter (transcriptToHighlights/formatTimestamp) reads `ts`,
// so without this every turn falls back to "0:00". `timestamp` may arrive as a
// number/numeric-string of seconds, an "mm:ss"/"hh:mm:ss" clock string, or an
// ISO datetime — handle each so the displayed offset is correct.
function tsFromTimestamp(timestamp: unknown): string | undefined {
    if (timestamp == null) return undefined;
    if (typeof timestamp === "number") {
        return Number.isFinite(timestamp) ? secondsToClock(timestamp) : undefined;
    }
    if (typeof timestamp === "string") {
        const t = timestamp.trim();
        if (!t) return undefined;
        if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(t)) return t; // already a clock string
        if (/^\d+(\.\d+)?$/.test(t)) return secondsToClock(Number(t)); // numeric seconds
        return t; // ISO / other — transcriptToHighlights anchors off the first turn
    }
    return undefined;
}

function normalizeTranscriptTiming(turns: TranscriptTurn[]): TranscriptTurn[] {
    return turns.map((turn) => {
        const r = turn as unknown as Record<string, unknown>;
        if (typeof r.ts === "string" && r.ts.trim()) return turn;
        const ts = tsFromTimestamp(
            r.timestamp ?? r.timestamp_secs ?? r.time_in_call_secs,
        );
        return ts ? { ...turn, ts } : turn;
    });
}

export async function getSessionResults(
    token: string
): Promise<SessionResultsResponse | null> {
    if (USE_MOCK) {
        const r = await getSessionByToken(token);
        if (!r?.session) return null;
        return { session: r.session, rubric_criteria: [] };
    }
    try {
        // authHeaders: includes JWT when present (employer view) and degrades
        // gracefully to no Authorization header when absent (employee token link).
        const r = await axios.get(
            `${apiURL}/training/session/${token}/results`,
            { headers: authHeaders(), timeout: 10000 }
        );
        const body = unwrap<{ session: TrainingSession; rubric_criteria?: RubricCriterion[] }>(r.data);
        if (!body?.session) return null;
        const session = body.session;
        if (Array.isArray(session.transcript)) {
            session.transcript = normalizeTranscriptTiming(session.transcript);
        }
        if (Array.isArray(session.transcript_full)) {
            session.transcript_full = normalizeTranscriptTiming(session.transcript_full);
        }
        return { session, rubric_criteria: body.rubric_criteria ?? [] };
    } catch {
        return null;
    }
}

export function getSessionAudioUrl(token: string): string {
    return `${apiURL}/training/session/${token}/audio`;
}

export async function getSessionHistory(
    token: string
): Promise<SessionAttempt[]> {
    if (USE_MOCK) {
        await sleep(150);
        return [
            {
                attempt_number: 1,
                status: "completed",
                total_score: 17,
                completed_at: new Date(Date.now() - 86400000).toISOString(),
                token,
            },
        ];
    }
    try {
        const r = await axios.get(
            `${apiURL}/training/sessions/${token}/history`,
            { headers: authHeaders(), timeout: 10000 }
        );
        return unwrap<SessionAttempt[]>(r.data) ?? [];
    } catch {
        return [];
    }
}

// --- CSV Export (client-side) ---

export function exportCampaignCSV(detail: TrainingCampaignDetail): void {
    const rows: string[][] = [
        ["Name", "Email", "Role", "Department", "Status", "Attempts", "Latest Score", "Completed At"],
    ];
    const latest = new Map<string, TrainingSession>();
    for (const s of detail.sessions) {
        if (s.is_test) continue;
        const prev = latest.get(s.employee_email);
        if (!prev || s.attempt_number > prev.attempt_number) {
            latest.set(s.employee_email, s);
        }
    }
    for (const s of Array.from(latest.values())) {
        rows.push([
            s.employee_name,
            s.employee_email,
            s.employee_role ?? "",
            s.employee_department ?? "",
            s.status,
            String(s.attempt_number),
            s.total_score !== null ? String(s.total_score) : "",
            s.completed_at ?? "",
        ]);
    }
    const csv = rows.map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${detail.name.replace(/[^a-zA-Z0-9]/g, "_")}_export.csv`;
    link.click();
    URL.revokeObjectURL(url);
}

export interface ReportsExportParams {
    format?: "xlsx" | "csv"; // default "xlsx"
    scope?: "workspace" | "campaign" | "manager"; // default "workspace"
    campaign_id?: string; // required when scope=campaign
    manager_id?: string; // required when scope=manager (needs ticket #1)
    days?: number; // completion-trend window; default 30
}

// Download the full Reports export. The BE generates the file (xlsx with one
// sheet per section, or csv); the FE only calls the endpoint and saves the
// returned blob. See docs/TRAINING_REPORTS_EXPORT_BE_HANDOFF_*.md for the
// endpoint contract and per-sheet layout.
export async function downloadReportsExport(
    params: ReportsExportParams = {},
): Promise<void> {
    const query = new URLSearchParams();
    query.set("format", params.format ?? "xlsx");
    if (params.scope) query.set("scope", params.scope);
    if (params.campaign_id) query.set("campaign_id", params.campaign_id);
    if (params.manager_id) query.set("manager_id", params.manager_id);
    if (params.days) query.set("days", String(params.days));

    const res = await axios.get(
        `${apiURL}/training/reports/export?${query.toString()}`,
        { headers: authHeaders(), responseType: "blob", timeout: 60000 },
    );

    // Prefer the server-provided filename (Content-Disposition); fall back to a
    // sensible default keyed on the requested format.
    const dispo = (res.headers?.["content-disposition"] ?? "") as string;
    const fromHeader = dispo.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i)?.[1];
    const ext = (params.format ?? "xlsx") === "csv" ? "csv" : "xlsx";
    const filename =
        (fromHeader && decodeURIComponent(fromHeader)) ||
        `training_report_${new Date().toISOString().slice(0, 10)}.${ext}`;

    const url = URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
}

export async function resendInvitation(
    campaignId: string,
    email: string
): Promise<boolean> {
    if (USE_MOCK) {
        await sleep(300);
        return true;
    }
    try {
        await axios.post(
            `${apiURL}/training/campaigns/${campaignId}/invitations/${encodeURIComponent(
                email
            )}/resend`,
            {},
            { headers: authHeaders(), timeout: 10000 }
        );
        return true;
    } catch {
        return false;
    }
}

// --- Team Leader lookup ---

export interface WorkspaceTeamLeader {
    user_id: string;
    name: string;
    email: string;
}

export async function getWorkspaceTeamLeaders(): Promise<WorkspaceTeamLeader[]> {
    try {
        const r = await axios.get(`${apiURL}/training/team-leaders`, {
            headers: authHeaders(),
            timeout: 10000,
        });
        const data = unwrap<WorkspaceTeamLeader[]>(r.data);
        return Array.isArray(data) ? data : [];
    } catch {
        return [];
    }
}

export async function getTeamLeaders(): Promise<string[]> {
    const leaders = await getWorkspaceTeamLeaders();
    return leaders.map((l) => l.name);
}

// --- Trainee bug report ---
// Calls POST /training/session/:token/bug-report — no auth header needed,
// the session token in the URL identifies the employee and campaign.

export interface TrainingBugReportRequest {
    bug_type: string;
    bug_description: string;
    meta?: Record<string, unknown>;
}

export async function makeTrainingBugReport(
    sessionToken: string,
    data: TrainingBugReportRequest,
): Promise<{ success: boolean; message?: string }> {
    try {
        const r = await axios.post(
            `${apiURL}/training/session/${sessionToken}/bug-report`,
            data,
            { timeout: 10000 },
        );
        const body = r.data as { code?: number; data?: { success?: boolean }; message?: string };
        if (body?.data?.success || body?.code === 201) {
            return { success: true };
        }
        return { success: false, message: body?.message ?? "Failed to submit bug report" };
    } catch (err: unknown) {
        const e = err as import("axios").AxiosError;
        return {
            success: false,
            message: (e.response?.data as { message?: string })?.message ?? "Failed to submit bug report",
        };
    }
}

// ─── Trainer email preferences ───────────────────────────────────────────────
// Per-user opt-out toggles for the three Trainer-facing notification emails.
// Backed by GET/PUT /training/email-preferences (BE #3192 / PR #3199). The
// model is OPT-OUT / default-on: every key is a real boolean, never null, and
// a user who never touched a preference receives all three emails.
export interface TrainerEmailPreferences {
    member_joined: boolean;
    trainee_completed: boolean;
    audio_upload: boolean;
}

// GET the caller's Trainer email preferences. Returns all three keys as
// booleans (BE resolves defaults to `true`).
export async function getTrainerEmailPreferences(): Promise<TrainerEmailPreferences> {
    const r = await axios.get(`${apiURL}/training/email-preferences`, {
        headers: authHeaders(),
        timeout: 10000,
    });
    return unwrap<TrainerEmailPreferences>(r.data);
}

// PUT a partial update of the caller's Trainer email preferences. Only the
// supplied keys change; the BE merges them over the current set and returns the
// full resolved object.
export async function updateTrainerEmailPreferences(
    prefs: Partial<TrainerEmailPreferences>,
): Promise<TrainerEmailPreferences> {
    const r = await axios.put(
        `${apiURL}/training/email-preferences`,
        prefs,
        { headers: authHeaders(), timeout: 10000 },
    );
    return unwrap<TrainerEmailPreferences>(r.data);
}
